// Hand-crafted map templates for each zone
// Diablo-style dungeon layouts with rooms and corridors

import { PropType } from '@client/assets/props';

// Local TileType enum to avoid circular dependency
export enum TileType {
  GRASS = 0,
  DIRT = 1,
  STONE = 2,
  WATER = 3,
  SAND = 4,
  DARK_GRASS = 5,
  MUD = 6,
  SNOW = 7,
  LAVA = 8,
  VOID = 9,
  // NEW tile types
  HILL = 10,           // Elevated grass, walkable but slower
  MOUNTAIN = 11,       // Rocky mountain, not walkable
  SHALLOW_WATER = 12,  // Walkable water (slower movement)
  DEEP_WATER = 13,     // Not walkable, deeper blue
  SWAMP_WATER = 14,    // Walkable, very slow, green tint
  CLIFF_EDGE = 15,     // Visual edge of elevation change
  ROCKY_GROUND = 16,   // Dark stone variation
}

export type BiomeType = 'forest' | 'swamp' | 'desert' | 'mountains' | 'underworld' | 'sacred' | 'hell';

export interface PropPlacement {
  type: PropType;
  x: number;
  y: number;
  variant?: number;
}

export interface SpawnPoint {
  type: 'player' | 'enemy' | 'boss' | 'portal' | 'npc';
  x: number;
  y: number;
}

export interface MapTemplate {
  biome: BiomeType;
  width: number;
  height: number;
  tiles: TileType[][];
  props: PropPlacement[];
  spawns?: SpawnPoint[];
}

// Helper to create a filled tile array
function createTileGrid(width: number, height: number, fill: TileType): TileType[][] {
  const grid: TileType[][] = [];
  for (let y = 0; y < height; y++) {
    grid[y] = [];
    for (let x = 0; x < width; x++) {
      grid[y][x] = fill;
    }
  }
  return grid;
}

// Helper to fill a rectangle
function fillRect(grid: TileType[][], x: number, y: number, w: number, h: number, tile: TileType): void {
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const ty = y + dy;
      const tx = x + dx;
      if (ty >= 0 && ty < grid.length && tx >= 0 && tx < grid[0].length) {
        grid[ty][tx] = tile;
      }
    }
  }
}

// Helper to fill a circle
function fillCircle(grid: TileType[][], cx: number, cy: number, r: number, tile: TileType): void {
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      if (dist <= r && y >= 0 && y < grid.length && x >= 0 && x < grid[0].length) {
        grid[y][x] = tile;
      }
    }
  }
}

// Helper to draw a corridor between two points
function fillCorridor(grid: TileType[][], x1: number, y1: number, x2: number, y2: number, width: number, tile: TileType): void {
  // L-shaped corridor: first horizontal, then vertical
  const midX = x2;
  const midY = y1;

  // Horizontal segment
  const startX = Math.min(x1, midX);
  const endX = Math.max(x1, midX);
  fillRect(grid, startX, y1 - Math.floor(width/2), endX - startX + 1, width, tile);

  // Vertical segment
  const startY = Math.min(midY, y2);
  const endY = Math.max(midY, y2);
  fillRect(grid, x2 - Math.floor(width/2), startY, width, endY - startY + 1, tile);
}

// Helper to fill an ellipse
function fillEllipse(grid: TileType[][], cx: number, cy: number, rx: number, ry: number, tile: TileType): void {
  for (let y = cy - ry; y <= cy + ry; y++) {
    for (let x = cx - rx; x <= cx + rx; x++) {
      const dist = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (dist <= 1 && y >= 0 && y < grid.length && x >= 0 && x < grid[0].length) {
        grid[y][x] = tile;
      }
    }
  }
}

// Helper to draw a winding path between two points with organic curves
function fillWindingPath(grid: TileType[][], x1: number, y1: number, x2: number, y2: number, width: number, tile: TileType, curvature: number = 0.3): void {
  const steps = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1)) * 2;
  const halfW = Math.floor(width / 2);

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    // Add sine wave offset for organic feel
    const offsetX = Math.sin(t * Math.PI * 2) * curvature * Math.abs(y2 - y1) * 0.15;
    const offsetY = Math.cos(t * Math.PI * 2) * curvature * Math.abs(x2 - x1) * 0.15;

    const px = Math.round(x1 + (x2 - x1) * t + offsetX);
    const py = Math.round(y1 + (y2 - y1) * t + offsetY);

    fillCircle(grid, px, py, halfW, tile);
  }
}

// ============================================
// OTAG KOYU - Village Hub (100x85)
// Open, welcoming village with wide paths, large clearings, minimal obstacles
// ============================================
function createOtagKoyuTemplate(): MapTemplate {
  const W = 100, H = 85;
  const tiles = createTileGrid(W, H, TileType.GRASS);

  // === LARGE VILLAGE GREEN (center-south) - extra wide open area ===
  fillCircle(tiles, 50, 48, 22, TileType.GRASS);
  fillCircle(tiles, 50, 48, 12, TileType.STONE);     // wide stone plaza core
  fillCircle(tiles, 50, 48, 5, TileType.DIRT);        // fire pit

  // === NORTH PATH to forest exit - wide and clear ===
  fillRect(tiles, 45, 3, 10, 30, TileType.DIRT);
  fillCircle(tiles, 50, 5, 8, TileType.GRASS);
  fillCircle(tiles, 50, 5, 4, TileType.DIRT);

  // === WEST DISTRICT - Elder & Houses - wider paths ===
  fillWindingPath(tiles, 34, 45, 15, 38, 6, TileType.DIRT, 0.15);
  fillCircle(tiles, 15, 38, 12, TileType.GRASS);
  fillCircle(tiles, 15, 38, 7, TileType.DIRT);
  // Side huts with open grass
  fillCircle(tiles, 8, 28, 8, TileType.GRASS);
  fillCircle(tiles, 8, 28, 4, TileType.DIRT);
  fillRect(tiles, 10, 28, 5, 8, TileType.DIRT);

  // === EAST DISTRICT - Market & Smithy - open market square ===
  fillWindingPath(tiles, 66, 45, 82, 38, 6, TileType.DIRT, 0.15);
  fillCircle(tiles, 82, 38, 12, TileType.GRASS);
  fillRect(tiles, 73, 30, 18, 16, TileType.DIRT);    // wider market square
  // Smithy alcove - more space
  fillCircle(tiles, 90, 28, 7, TileType.GRASS);
  fillCircle(tiles, 90, 28, 4, TileType.DIRT);
  fillRect(tiles, 86, 28, 4, 7, TileType.DIRT);

  // === SW DISTRICT - Guard Camp - wider approach ===
  fillWindingPath(tiles, 38, 58, 22, 68, 5, TileType.DIRT, 0.1);
  fillCircle(tiles, 22, 68, 11, TileType.GRASS);
  fillCircle(tiles, 22, 68, 6, TileType.DIRT);

  // === SE DISTRICT - Healer's Garden & Pond - open garden ===
  fillWindingPath(tiles, 62, 58, 78, 68, 5, TileType.DIRT, 0.1);
  fillCircle(tiles, 78, 68, 11, TileType.GRASS);
  fillCircle(tiles, 78, 68, 6, TileType.DIRT);
  fillCircle(tiles, 86, 73, 3, TileType.SHALLOW_WATER);
  fillCircle(tiles, 87, 74, 1, TileType.WATER);

  // === STREAM (NW to SE) - gentle, narrow, not blocking ===
  fillWindingPath(tiles, 5, 10, 25, 25, 1, TileType.SHALLOW_WATER, 0.2);
  fillWindingPath(tiles, 25, 25, 42, 38, 1, TileType.SHALLOW_WATER, 0.2);

  // === GENTLE HILLS (far edges only, not blocking paths) ===
  fillCircle(tiles, 8, 8, 4, TileType.HILL);
  fillCircle(tiles, 92, 8, 4, TileType.HILL);

  // === EXTRA OPEN GRASS between districts for easy navigation ===
  fillCircle(tiles, 35, 55, 5, TileType.GRASS);
  fillCircle(tiles, 65, 55, 5, TileType.GRASS);

  const props: PropPlacement[] = [
    // === PLAZA ===
    { type: 'well', x: 50, y: 50 },
    { type: 'campfire', x: 48, y: 46 },
    { type: 'torch', x: 42, y: 38 },
    { type: 'torch', x: 58, y: 38 },
    { type: 'torch', x: 42, y: 58 },
    { type: 'torch', x: 58, y: 58 },
    { type: 'sign', x: 53, y: 38 },
    // === WEST - Elder Houses ===
    { type: 'house', x: 12, y: 35, variant: 0 },
    { type: 'house', x: 18, y: 41, variant: 1 },
    { type: 'house', x: 6, y: 25, variant: 0 },
    { type: 'barrel', x: 10, y: 40 },
    { type: 'crate', x: 20, y: 33 },
    { type: 'torch', x: 8, y: 34 },
    // === EAST - Market ===
    { type: 'tent', x: 78, y: 34, variant: 0 },
    { type: 'tent', x: 84, y: 40, variant: 1 },
    { type: 'cart', x: 86, y: 35 },
    { type: 'barrel', x: 76, y: 38 },
    { type: 'crate', x: 82, y: 32 },
    { type: 'torch', x: 74, y: 32 },
    { type: 'torch', x: 88, y: 26 },
    // === SW - Guard Camp ===
    { type: 'tent', x: 19, y: 66, variant: 1 },
    { type: 'tent', x: 26, y: 71, variant: 2 },
    { type: 'campfire', x: 22, y: 71 },
    { type: 'barrel', x: 17, y: 70 },
    { type: 'crate', x: 28, y: 65 },
    // === SE - Healer Garden ===
    { type: 'bush', x: 74, y: 65, variant: 0 },
    { type: 'bush', x: 82, y: 72, variant: 1 },
    { type: 'rock', x: 86, y: 75, variant: 0 },
    { type: 'rock', x: 72, y: 72, variant: 1 },
    // === NORTH EXIT ===
    { type: 'sign', x: 53, y: 5 },
    { type: 'torch', x: 45, y: 8 },
    { type: 'torch', x: 55, y: 8 },
    // === TREES (sparse border) ===
    { type: 'tree_oak', x: 8, y: 14, variant: 0 },
    { type: 'tree_pine', x: 28, y: 5, variant: 1 },
    { type: 'tree_oak', x: 68, y: 8, variant: 0 },
    { type: 'tree_pine', x: 92, y: 18, variant: 1 },
    { type: 'tree_oak', x: 5, y: 55, variant: 1 },
    { type: 'tree_pine', x: 94, y: 55, variant: 0 },
    { type: 'tree_oak', x: 40, y: 78, variant: 0 },
    { type: 'tree_pine', x: 60, y: 80, variant: 1 },
    { type: 'tree_oak', x: 10, y: 78, variant: 0 },
    { type: 'tree_pine', x: 92, y: 78, variant: 1 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 50, y: 48 },
    { type: 'npc', x: 50, y: 44 },     // Kam Batur (plaza)
    { type: 'npc', x: 80, y: 36 },     // Demirci Temir (market)
    { type: 'npc', x: 84, y: 40 },     // Tacir Altin (market)
    { type: 'npc', x: 15, y: 36 },     // Bilge Koca (elder)
    { type: 'npc', x: 22, y: 70 },     // Muhafiz Boru (guard)
    { type: 'npc', x: 76, y: 68 },     // Otaci Yesil (healer)
    { type: 'portal', x: 50, y: 3 },   // North exit to forest
  ];

  return { biome: 'sacred', width: W, height: H, tiles, props, spawns };
}

// ============================================
// KARANLIK ORMAN - Dark Forest (130x110)
// Dense, winding paths through DARK_GRASS forest: narrow trails, thick canopy feel
// Paths are narrower (3 wide), more curves, dark grass dominates
// ============================================
export function createKaranlikOrmanTemplate(): MapTemplate {
  const W = 130, H = 110;
  const tiles = createTileGrid(W, H, TileType.DARK_GRASS);

  // === ENTRANCE (SW, near tile 21,101) ===
  fillCircle(tiles, 21, 98, 8, TileType.GRASS);
  fillCircle(tiles, 21, 98, 4, TileType.DIRT);

  // === PATH: Entrance → First clearing - narrow and winding ===
  fillWindingPath(tiles, 21, 90, 35, 78, 3, TileType.DIRT, 0.5);

  // === FIRST CLEARING (~35,75) - small respite area ===
  fillCircle(tiles, 35, 75, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 35, 75, 5, TileType.DIRT);

  // === PATH: First clearing → Center crossroads - very winding ===
  fillWindingPath(tiles, 40, 70, 52, 62, 3, TileType.DIRT, 0.6);
  fillWindingPath(tiles, 52, 62, 60, 55, 3, TileType.DIRT, 0.5);

  // === CENTER CROSSROADS (~65,50) ===
  fillCircle(tiles, 65, 50, 10, TileType.DARK_GRASS);
  fillCircle(tiles, 65, 50, 6, TileType.DIRT);

  // === PATH: Center → NE exit - winding forest trail ===
  fillWindingPath(tiles, 72, 42, 80, 32, 3, TileType.DIRT, 0.5);
  fillWindingPath(tiles, 80, 32, 90, 22, 3, TileType.DIRT, 0.4);
  fillWindingPath(tiles, 90, 22, 100, 12, 3, TileType.DIRT, 0.3);

  // === NE EXIT clearing (~100,6) ===
  fillCircle(tiles, 100, 6, 7, TileType.DARK_GRASS);
  fillCircle(tiles, 100, 6, 4, TileType.DIRT);

  // === ABANDONED CAMP (NW, ~30,35) - hidden off the main path ===
  fillWindingPath(tiles, 58, 46, 45, 40, 3, TileType.DIRT, 0.4);
  fillWindingPath(tiles, 45, 40, 30, 35, 3, TileType.DIRT, 0.3);
  fillCircle(tiles, 30, 35, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 30, 35, 4, TileType.DIRT);

  // === GRAVEYARD (N, ~65,20) - eerie clearing ===
  fillRect(tiles, 63, 32, 4, 10, TileType.DIRT);
  fillCircle(tiles, 65, 20, 7, TileType.DARK_GRASS);
  fillCircle(tiles, 65, 20, 4, TileType.DIRT);

  // === DARK POND (E, ~100,55) - murky forest pool ===
  fillWindingPath(tiles, 76, 52, 90, 55, 3, TileType.DIRT, 0.4);
  fillCircle(tiles, 100, 55, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 103, 57, 4, TileType.SWAMP_WATER);
  fillCircle(tiles, 97, 52, 4, TileType.DARK_GRASS);

  // === WOLF DEN (NW, ~18,20) - rocky and dark ===
  fillCircle(tiles, 18, 20, 8, TileType.ROCKY_GROUND);
  fillCircle(tiles, 18, 20, 4, TileType.DIRT);
  fillWindingPath(tiles, 26, 32, 20, 25, 3, TileType.DIRT, 0.4);

  // === SOUTH EXIT clearing (back to village, near tile 21,101) ===
  fillCircle(tiles, 21, 105, 5, TileType.GRASS);
  fillRect(tiles, 19, 102, 5, 4, TileType.DIRT);

  // === DARK STREAM (N-S through the map) - murky and narrow ===
  fillWindingPath(tiles, 50, 5, 52, 30, 1, TileType.SHALLOW_WATER, 0.4);
  fillWindingPath(tiles, 52, 30, 48, 60, 1, TileType.SHALLOW_WATER, 0.4);
  fillWindingPath(tiles, 48, 60, 45, 95, 1, TileType.SHALLOW_WATER, 0.4);

  // === ROCKY OUTCROPS (scattered, creating natural barriers) ===
  fillCircle(tiles, 60, 30, 4, TileType.HILL);
  fillCircle(tiles, 72, 28, 3, TileType.HILL);

  // === HIDDEN SIDE CLEARING (south-east) - off beaten path ===
  fillCircle(tiles, 85, 80, 6, TileType.DARK_GRASS);
  fillWindingPath(tiles, 70, 58, 82, 75, 3, TileType.DIRT, 0.4);
  fillCircle(tiles, 85, 80, 3, TileType.DIRT);

  // === SMALL WIDENINGS along paths (just enough to breathe) ===
  fillCircle(tiles, 28, 85, 3, TileType.DARK_GRASS);
  fillCircle(tiles, 78, 40, 3, TileType.DARK_GRASS);
  fillCircle(tiles, 50, 68, 2, TileType.DARK_GRASS);

  const props: PropPlacement[] = [
    // === ENTRANCE ===
    { type: 'sign', x: 24, y: 95 },
    { type: 'torch', x: 16, y: 98 },
    { type: 'torch', x: 26, y: 98 },
    // === FIRST CLEARING ===
    { type: 'rock', x: 32, y: 72, variant: 0 },
    { type: 'rock', x: 38, y: 78, variant: 1 },
    { type: 'bush', x: 30, y: 78, variant: 0 },
    // === CENTER ===
    { type: 'rock', x: 62, y: 47, variant: 0 },
    { type: 'rock', x: 70, y: 53, variant: 1 },
    { type: 'bush', x: 58, y: 53, variant: 0 },
    { type: 'torch', x: 68, y: 42 },
    { type: 'torch', x: 60, y: 56 },
    // === ABANDONED CAMP ===
    { type: 'tent', x: 28, y: 33, variant: 2 },
    { type: 'campfire', x: 33, y: 37 },
    { type: 'crate', x: 26, y: 38 },
    { type: 'barrel', x: 35, y: 32 },
    // === GRAVEYARD ===
    { type: 'grave', x: 62, y: 18 },
    { type: 'grave', x: 65, y: 22 },
    { type: 'grave', x: 68, y: 19 },
    { type: 'grave', x: 64, y: 15 },
    { type: 'tree_dead', x: 58, y: 22, variant: 0 },
    { type: 'tree_dead', x: 70, y: 16, variant: 1 },
    // === DARK POND ===
    { type: 'tree_dead', x: 95, y: 52, variant: 1 },
    { type: 'rock', x: 105, y: 60, variant: 2 },
    { type: 'bush', x: 96, y: 59, variant: 1 },
    // === NE EXIT ===
    { type: 'sign', x: 103, y: 4 },
    { type: 'torch', x: 95, y: 6 },
    { type: 'torch', x: 105, y: 6 },
    // === WOLF DEN ===
    { type: 'rock', x: 15, y: 18, variant: 0 },
    { type: 'rock', x: 21, y: 17, variant: 1 },
    { type: 'rock', x: 14, y: 23, variant: 2 },
    { type: 'bush', x: 22, y: 23, variant: 0 },
    // === SE CLEARING ===
    { type: 'rock', x: 83, y: 78, variant: 0 },
    { type: 'bush', x: 88, y: 82, variant: 1 },
    // === DENSE TREES along paths (dark, oppressive canopy) ===
    { type: 'tree_oak', x: 10, y: 75, variant: 0 },
    { type: 'tree_pine', x: 38, y: 58, variant: 1 },
    { type: 'tree_oak', x: 80, y: 68, variant: 0 },
    { type: 'tree_pine', x: 55, y: 85, variant: 1 },
    { type: 'tree_dead', x: 88, y: 42, variant: 0 },
    { type: 'tree_oak', x: 15, y: 55, variant: 1 },
    { type: 'tree_pine', x: 110, y: 35, variant: 0 },
    { type: 'tree_dead', x: 35, y: 55, variant: 1 },
    { type: 'tree_oak', x: 75, y: 75, variant: 0 },
    { type: 'tree_pine', x: 8, y: 95, variant: 1 },
    { type: 'tree_dead', x: 115, y: 20, variant: 0 },
    { type: 'tree_pine', x: 45, y: 15, variant: 1 },
    // === EXTRA DENSE TREE CLUSTERS (forest feeling) ===
    { type: 'tree_pine', x: 12, y: 85, variant: 0 },
    { type: 'tree_oak', x: 16, y: 90, variant: 1 },
    { type: 'tree_pine', x: 6, y: 80, variant: 0 },
    { type: 'tree_dead', x: 28, y: 65, variant: 0 },
    { type: 'tree_pine', x: 46, y: 72, variant: 1 },
    { type: 'tree_oak', x: 52, y: 78, variant: 0 },
    { type: 'tree_pine', x: 42, y: 48, variant: 1 },
    { type: 'tree_dead', x: 48, y: 42, variant: 0 },
    { type: 'tree_pine', x: 76, y: 60, variant: 0 },
    { type: 'tree_oak', x: 82, y: 50, variant: 1 },
    { type: 'tree_pine', x: 90, y: 65, variant: 0 },
    { type: 'tree_dead', x: 95, y: 40, variant: 1 },
    { type: 'tree_pine', x: 108, y: 25, variant: 0 },
    { type: 'tree_oak', x: 112, y: 45, variant: 1 },
    { type: 'tree_pine', x: 70, y: 15, variant: 0 },
    { type: 'tree_dead', x: 55, y: 25, variant: 1 },
    { type: 'tree_pine', x: 40, y: 18, variant: 0 },
    { type: 'tree_oak', x: 25, y: 48, variant: 1 },
    { type: 'tree_dead', x: 18, y: 42, variant: 0 },
    { type: 'tree_pine', x: 105, y: 70, variant: 1 },
    { type: 'tree_oak', x: 120, y: 55, variant: 0 },
    { type: 'tree_pine', x: 5, y: 35, variant: 1 },
    { type: 'tree_dead', x: 10, y: 15, variant: 0 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 21, y: 98 },
    // === ENTRANCE (easy) ===
    { type: 'enemy', x: 26, y: 92 },
    { type: 'enemy', x: 30, y: 85 },
    // === FIRST CLEARING ===
    { type: 'enemy', x: 33, y: 73 },
    { type: 'enemy', x: 38, y: 77 },
    { type: 'enemy', x: 35, y: 70 },
    // === MAIN PATH ===
    { type: 'enemy', x: 50, y: 65 },
    { type: 'enemy', x: 55, y: 60 },
    // === CENTER ===
    { type: 'enemy', x: 63, y: 48 },
    { type: 'enemy', x: 68, y: 52 },
    { type: 'enemy', x: 65, y: 44 },
    // === ABANDONED CAMP ===
    { type: 'enemy', x: 29, y: 33 },
    { type: 'enemy', x: 34, y: 38 },
    // === GRAVEYARD ===
    { type: 'enemy', x: 63, y: 19 },
    { type: 'enemy', x: 67, y: 22 },
    // === DARK POND ===
    { type: 'enemy', x: 98, y: 53 },
    { type: 'enemy', x: 102, y: 57 },
    { type: 'enemy', x: 99, y: 57 },
    // === NE EXIT ===
    { type: 'enemy', x: 97, y: 8 },
    { type: 'enemy', x: 103, y: 8 },
    // === WOLF DEN ===
    { type: 'enemy', x: 16, y: 19 },
    { type: 'enemy', x: 20, y: 22 },
    { type: 'enemy', x: 18, y: 16 },
    // === SE CLEARING ===
    { type: 'enemy', x: 84, y: 79 },
    { type: 'enemy', x: 87, y: 82 },
    // === PORTALS ===
    { type: 'portal', x: 100, y: 4 },   // NE exit to swamp
    { type: 'portal', x: 21, y: 101 },   // South exit back to village
  ];

  return { biome: 'forest', width: W, height: H, tiles, props, spawns };
}

// ============================================
// RUH BATAKLIGI - Spirit Swamp (120x100)
// Archipelago of islands in deep swamp water, connected by narrow mud bridges
// More water coverage, smaller islands, treacherous crossings
// ============================================
function createRuhBatakligiTemplate(): MapTemplate {
  const W = 120, H = 100;
  const tiles = createTileGrid(W, H, TileType.SWAMP_WATER);

  // === DEEP WATER channels - wider, more dangerous ===
  fillRect(tiles, 53, 0, 8, 100, TileType.DEEP_WATER);    // Wide N-S river
  fillRect(tiles, 0, 46, 120, 7, TileType.DEEP_WATER);     // Wide E-W river
  // Extra deep water pools
  fillCircle(tiles, 35, 70, 5, TileType.DEEP_WATER);
  fillCircle(tiles, 85, 35, 4, TileType.DEEP_WATER);
  fillCircle(tiles, 45, 15, 3, TileType.DEEP_WATER);

  // === ISLAND 1 - ENTRANCE (S, near tile 69,98) ===
  fillCircle(tiles, 65, 85, 9, TileType.DARK_GRASS);
  fillCircle(tiles, 65, 85, 4, TileType.MUD);
  // Narrow bridge to center
  fillRect(tiles, 64, 68, 3, 12, TileType.MUD);

  // === ISLAND 2 - CENTER HUB (~60,58) ===
  fillCircle(tiles, 60, 58, 11, TileType.DARK_GRASS);
  fillCircle(tiles, 60, 58, 5, TileType.MUD);
  // Narrow bridges (3-wide instead of 4)
  fillRect(tiles, 29, 57, 21, 3, TileType.MUD);    // Bridge west
  fillRect(tiles, 59, 31, 3, 19, TileType.MUD);     // Bridge north
  fillRect(tiles, 71, 57, 22, 3, TileType.MUD);     // Bridge east

  // === ISLAND 3 - WEST CAMP (~20,58) ===
  fillCircle(tiles, 20, 58, 9, TileType.DARK_GRASS);
  fillCircle(tiles, 20, 58, 5, TileType.MUD);

  // === ISLAND 4 - NORTH (~60,22) ===
  fillCircle(tiles, 60, 22, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 60, 22, 4, TileType.MUD);
  // Narrow bridges
  fillRect(tiles, 36, 21, 15, 3, TileType.MUD);     // Bridge to graveyard
  fillRect(tiles, 68, 19, 17, 3, TileType.MUD);      // Bridge to ruins

  // === ISLAND 5 - MUNZEVI (E, ~98,58) ===
  fillCircle(tiles, 98, 58, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 98, 58, 4, TileType.MUD);

  // === ISLAND 6 - GRAVEYARD (NW, ~28,18) - small and eerie ===
  fillCircle(tiles, 28, 18, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 28, 18, 4, TileType.MUD);

  // === ISLAND 7 - RUINS/EXIT (NE, near tile 99,5) ===
  fillCircle(tiles, 92, 12, 8, TileType.DARK_GRASS);
  fillCircle(tiles, 92, 12, 5, TileType.STONE);
  fillCircle(tiles, 92, 12, 2, TileType.ROCKY_GROUND);

  // === ISLAND 8 - SECRET (SW) - hard to reach ===
  fillCircle(tiles, 14, 82, 6, TileType.DARK_GRASS);
  fillRect(tiles, 20, 83, 30, 3, TileType.MUD);

  // === SMALL MUD ISLETS (stepping stones, atmosphere) ===
  fillCircle(tiles, 42, 65, 2, TileType.MUD);
  fillCircle(tiles, 78, 42, 2, TileType.MUD);
  fillCircle(tiles, 50, 78, 2, TileType.MUD);
  fillCircle(tiles, 105, 25, 2, TileType.MUD);

  // === SOUTH EXIT (back to forest, near tile 69,98) ===
  fillCircle(tiles, 69, 95, 4, TileType.GRASS);
  fillRect(tiles, 67, 90, 3, 5, TileType.MUD);

  // === BOSS ARENA (island north of hub) - isolated ===
  fillCircle(tiles, 40, 35, 9, TileType.DARK_GRASS);
  fillCircle(tiles, 40, 35, 4, TileType.MUD);
  fillRect(tiles, 49, 34, 6, 3, TileType.MUD);   // narrow bridge from north island

  const props: PropPlacement[] = [
    // === ISLAND 1 - Entrance ===
    { type: 'torch', x: 60, y: 88 },
    { type: 'torch', x: 70, y: 88 },
    { type: 'sign', x: 65, y: 82 },
    { type: 'rock', x: 62, y: 82, variant: 0 },
    { type: 'tree_dead', x: 58, y: 87, variant: 0 },
    // === ISLAND 2 - Hub ===
    { type: 'torch', x: 53, y: 54 },
    { type: 'torch', x: 67, y: 54 },
    { type: 'rock', x: 57, y: 62, variant: 0 },
    { type: 'tree_dead', x: 63, y: 52, variant: 0 },
    { type: 'rock', x: 65, y: 62, variant: 1 },
    { type: 'bush', x: 53, y: 60, variant: 0 },
    // === ISLAND 3 - West camp ===
    { type: 'tent', x: 17, y: 56, variant: 2 },
    { type: 'campfire', x: 23, y: 60 },
    { type: 'barrel', x: 15, y: 61 },
    { type: 'crate', x: 15, y: 54 },
    { type: 'tree_dead', x: 25, y: 64, variant: 1 },
    // === ISLAND 4 - North ===
    { type: 'sign', x: 62, y: 18 },
    { type: 'torch', x: 56, y: 26 },
    { type: 'tree_dead', x: 56, y: 19, variant: 0 },
    { type: 'rock', x: 64, y: 26, variant: 1 },
    // === ISLAND 5 - Munzevi (NPC safe) ===
    { type: 'tent', x: 96, y: 56, variant: 0 },
    { type: 'campfire', x: 101, y: 60 },
    { type: 'rock', x: 103, y: 62, variant: 0 },
    { type: 'torch', x: 93, y: 55 },
    { type: 'torch', x: 103, y: 55 },
    // === ISLAND 6 - Graveyard ===
    { type: 'grave', x: 25, y: 15 },
    { type: 'grave', x: 28, y: 18 },
    { type: 'grave', x: 31, y: 16 },
    { type: 'grave', x: 27, y: 21 },
    { type: 'grave', x: 30, y: 22 },
    { type: 'tree_dead', x: 22, y: 21, variant: 1 },
    { type: 'tree_dead', x: 33, y: 14, variant: 0 },
    { type: 'rock', x: 22, y: 15, variant: 0 },
    // === ISLAND 7 - Ruins/Exit ===
    { type: 'ruins', x: 90, y: 10, variant: 0 },
    { type: 'ruins', x: 95, y: 14, variant: 1 },
    { type: 'torch', x: 87, y: 9 },
    { type: 'torch', x: 97, y: 9 },
    { type: 'rock', x: 88, y: 14, variant: 0 },
    // === ISLAND 8 - Secret ===
    { type: 'crate', x: 12, y: 80 },
    { type: 'barrel', x: 16, y: 84 },
    { type: 'torch', x: 11, y: 79 },
    // === BOSS ARENA ===
    { type: 'tree_dead', x: 36, y: 33, variant: 0 },
    { type: 'tree_dead', x: 44, y: 33, variant: 1 },
    { type: 'rock', x: 40, y: 39, variant: 1 },
    // === BRIDGE SIGNS ===
    { type: 'sign', x: 64, y: 75 },
    { type: 'sign', x: 38, y: 58 },
    { type: 'sign', x: 80, y: 58 },
    // === SCATTERED DEAD TREES ===
    { type: 'tree_dead', x: 45, y: 40, variant: 0 },
    { type: 'tree_dead', x: 80, y: 75, variant: 1 },
    { type: 'tree_dead', x: 40, y: 75, variant: 0 },
    { type: 'tree_dead', x: 85, y: 30, variant: 1 },
    { type: 'tree_dead', x: 75, y: 85, variant: 0 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 65, y: 85 },
    // === Hub island ===
    { type: 'enemy', x: 56, y: 56 },
    { type: 'enemy', x: 64, y: 60 },
    { type: 'enemy', x: 60, y: 53 },
    { type: 'enemy', x: 63, y: 56 },
    // === West camp island ===
    { type: 'enemy', x: 17, y: 54 },
    { type: 'enemy', x: 23, y: 62 },
    { type: 'enemy', x: 19, y: 62 },
    { type: 'npc', x: 20, y: 58 },    // Kayip Izci
    // === North island ===
    { type: 'enemy', x: 57, y: 20 },
    { type: 'enemy', x: 63, y: 24 },
    { type: 'enemy', x: 60, y: 18 },
    // === Munzevi (safe) ===
    { type: 'npc', x: 98, y: 58 },    // Kara Gulle
    // === Graveyard ===
    { type: 'enemy', x: 25, y: 16 },
    { type: 'enemy', x: 30, y: 20 },
    { type: 'enemy', x: 27, y: 22 },
    { type: 'enemy', x: 32, y: 16 },
    // === Ruins ===
    { type: 'enemy', x: 90, y: 11 },
    { type: 'enemy', x: 95, y: 14 },
    { type: 'enemy', x: 92, y: 16 },
    // === Secret ===
    { type: 'enemy', x: 14, y: 82 },
    // === Boss ===
    { type: 'boss', x: 40, y: 35 },
    // === Bridge ambush ===
    { type: 'enemy', x: 64, y: 72 },
    { type: 'enemy', x: 40, y: 57 },
    // === PORTALS ===
    { type: 'portal', x: 69, y: 98 },   // South exit to forest
    { type: 'portal', x: 99, y: 5 },    // NE exit to temple
  ];

  return { biome: 'swamp', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ERLIK TAPINAGI - Temple of Erlik (100x120)
// Structured dungeon: entrance hall → long corridor → courtyard → side chambers
// → inner sanctum corridors → boss arena. Tight corridors, right angles, dungeon feel.
// ============================================
function createErlikTapinagiTemplate(): MapTemplate {
  const W = 100, H = 120;
  const tiles = createTileGrid(W, H, TileType.VOID);

  // === ENTRANCE HALL (S, near tile 49,113) ===
  fillRect(tiles, 37, 107, 26, 12, TileType.STONE);      // rectangular entrance hall
  fillRect(tiles, 41, 109, 18, 8, TileType.ROCKY_GROUND); // inner floor

  // === MAIN CORRIDOR (S→center, y:68-107) - narrow with alcoves ===
  fillRect(tiles, 44, 68, 12, 40, TileType.STONE);
  fillRect(tiles, 46, 70, 8, 38, TileType.ROCKY_GROUND);
  // Side alcoves along corridor (architectural detail)
  fillRect(tiles, 38, 85, 6, 5, TileType.STONE);   // W alcove 1
  fillRect(tiles, 56, 85, 6, 5, TileType.STONE);   // E alcove 1
  fillRect(tiles, 38, 95, 6, 5, TileType.STONE);   // W alcove 2
  fillRect(tiles, 56, 95, 6, 5, TileType.STONE);   // E alcove 2

  // === OUTER COURTYARD (~49,62) - rectangular with pillars ===
  fillRect(tiles, 30, 52, 40, 20, TileType.STONE);
  fillRect(tiles, 34, 55, 32, 14, TileType.ROCKY_GROUND);

  // === WEST CHAMBER (~16,55) - connected by L-shaped corridor ===
  fillCorridor(tiles, 30, 62, 16, 55, 5, TileType.STONE);
  fillRect(tiles, 6, 47, 20, 16, TileType.STONE);         // rectangular room
  fillRect(tiles, 9, 50, 14, 10, TileType.ROCKY_GROUND);

  // === EAST CHAMBER (~82,55) - connected by L-shaped corridor ===
  fillCorridor(tiles, 70, 62, 82, 55, 5, TileType.STONE);
  fillRect(tiles, 74, 47, 20, 16, TileType.STONE);        // rectangular room
  fillRect(tiles, 77, 50, 14, 10, TileType.ROCKY_GROUND);

  // === SECRET TOMB (W, ~12,85) - hidden side passage ===
  fillRect(tiles, 12, 62, 4, 22, TileType.STONE);         // narrow corridor
  fillRect(tiles, 6, 84, 16, 12, TileType.STONE);         // rectangular tomb
  fillRect(tiles, 9, 87, 10, 6, TileType.ROCKY_GROUND);

  // === LAVA CHANNELS (architectural, lining the corridors) ===
  fillRect(tiles, 27, 40, 2, 28, TileType.LAVA);   // W lava channel
  fillRect(tiles, 71, 40, 2, 28, TileType.LAVA);   // E lava channel
  fillRect(tiles, 30, 76, 14, 2, TileType.LAVA);   // SW cross lava
  fillRect(tiles, 56, 76, 14, 2, TileType.LAVA);   // SE cross lava
  // Restore corridor over lava
  fillRect(tiles, 44, 76, 12, 2, TileType.STONE);

  // === INNER SANCTUM CORRIDOR (~49, y:30-52) - structured passage ===
  fillRect(tiles, 34, 30, 32, 22, TileType.STONE);
  fillRect(tiles, 38, 33, 24, 16, TileType.ROCKY_GROUND);
  // Inner corridor narrowing toward boss
  fillRect(tiles, 42, 30, 16, 3, TileType.ROCKY_GROUND);

  // === PATH TO BOSS ARENA - narrow ceremonial approach ===
  fillRect(tiles, 43, 10, 14, 22, TileType.STONE);
  fillRect(tiles, 45, 12, 10, 18, TileType.ROCKY_GROUND);

  // === BOSS ARENA (~49, y:2-22) - grand circular chamber ===
  fillCircle(tiles, 49, 14, 16, TileType.STONE);
  // Decorative rocky ring (walkable, architectural border)
  for (let y = -16; y <= 16; y++) {
    for (let x = -16; x <= 16; x++) {
      const dist = Math.sqrt(x * x + y * y);
      if (dist >= 12 && dist <= 14) {
        const ty = 14 + y, tx = 49 + x;
        if (ty >= 0 && ty < H && tx >= 0 && tx < W) {
          tiles[ty][tx] = TileType.ROCKY_GROUND;
        }
      }
    }
  }
  fillCircle(tiles, 49, 14, 12, TileType.STONE);
  // Lava accents at arena edges (decorative)
  fillRect(tiles, 33, 12, 2, 4, TileType.LAVA);
  fillRect(tiles, 65, 12, 2, 4, TileType.LAVA);
  // Boss throne platform
  fillRect(tiles, 42, 2, 14, 6, TileType.STONE);
  fillRect(tiles, 45, 3, 8, 4, TileType.ROCKY_GROUND);

  // === ROCKY DOORWAY connectors (between courtyard and corridors) ===
  fillRect(tiles, 30, 58, 6, 5, TileType.ROCKY_GROUND);
  fillRect(tiles, 64, 58, 6, 5, TileType.ROCKY_GROUND);

  const props: PropPlacement[] = [
    // === ENTRANCE HALL ===
    { type: 'sign', x: 53, y: 112 },
    { type: 'torch', x: 38, y: 110 },
    { type: 'torch', x: 62, y: 110 },
    { type: 'statue', x: 39, y: 108, variant: 1 },
    { type: 'statue', x: 61, y: 108, variant: 1 },
    // === CORRIDOR - lined with statues and torches ===
    { type: 'statue', x: 40, y: 100, variant: 1 },
    { type: 'statue', x: 58, y: 100, variant: 1 },
    { type: 'torch', x: 40, y: 95 },
    { type: 'torch', x: 58, y: 95 },
    { type: 'statue', x: 40, y: 90, variant: 0 },
    { type: 'statue', x: 58, y: 90, variant: 0 },
    { type: 'torch', x: 40, y: 85 },
    { type: 'torch', x: 58, y: 85 },
    { type: 'statue', x: 40, y: 80, variant: 0 },
    { type: 'statue', x: 58, y: 80, variant: 0 },
    { type: 'torch', x: 40, y: 75 },
    { type: 'torch', x: 58, y: 75 },
    // === ALCOVE DETAILS ===
    { type: 'crate', x: 39, y: 87 },
    { type: 'barrel', x: 57, y: 87 },
    { type: 'grave', x: 39, y: 97 },
    { type: 'grave', x: 57, y: 97 },
    // === COURTYARD ===
    { type: 'torch', x: 32, y: 55 },
    { type: 'torch', x: 68, y: 55 },
    { type: 'torch', x: 32, y: 68 },
    { type: 'torch', x: 68, y: 68 },
    { type: 'ruins', x: 49, y: 56, variant: 0 },
    { type: 'rock', x: 36, y: 65, variant: 0 },
    { type: 'rock', x: 62, y: 65, variant: 1 },
    // === WEST CHAMBER ===
    { type: 'ruins', x: 15, y: 54, variant: 1 },
    { type: 'torch', x: 10, y: 50 },
    { type: 'torch', x: 22, y: 50 },
    { type: 'grave', x: 13, y: 59 },
    { type: 'grave', x: 19, y: 59 },
    { type: 'barrel', x: 11, y: 53 },
    // === EAST CHAMBER ===
    { type: 'ruins', x: 81, y: 54, variant: 1 },
    { type: 'torch', x: 76, y: 50 },
    { type: 'torch', x: 88, y: 50 },
    { type: 'crate', x: 79, y: 59 },
    { type: 'barrel', x: 86, y: 59 },
    // === SECRET TOMB ===
    { type: 'grave', x: 12, y: 86 },
    { type: 'grave', x: 16, y: 90 },
    { type: 'crate', x: 11, y: 83 },
    { type: 'torch', x: 14, y: 82 },
    // === TEMPLE PLATFORM ===
    { type: 'ruins', x: 49, y: 38, variant: 0 },
    { type: 'ruins', x: 40, y: 44, variant: 1 },
    { type: 'ruins', x: 58, y: 44, variant: 1 },
    { type: 'torch', x: 34, y: 32 },
    { type: 'torch', x: 64, y: 32 },
    // === BOSS ARENA ===
    { type: 'statue', x: 49, y: 6, variant: 0 },
    { type: 'statue', x: 49, y: 22, variant: 0 },
    { type: 'statue', x: 38, y: 14, variant: 0 },
    { type: 'statue', x: 60, y: 14, variant: 0 },
    { type: 'statue', x: 44, y: 4, variant: 1 },
    { type: 'statue', x: 54, y: 4, variant: 1 },
    { type: 'torch', x: 40, y: 6 },
    { type: 'torch', x: 58, y: 6 },
    { type: 'torch', x: 40, y: 22 },
    { type: 'torch', x: 58, y: 22 },
    // === LAVA WARNING ROCKS ===
    { type: 'rock', x: 22, y: 42, variant: 2 },
    { type: 'rock', x: 75, y: 42, variant: 2 },
    // === ENTRANCE TREES ===
    { type: 'tree_dead', x: 10, y: 106, variant: 0 },
    { type: 'tree_dead', x: 88, y: 106, variant: 1 },
    { type: 'tree_oak', x: 38, y: 115, variant: 0 },
    { type: 'tree_oak', x: 62, y: 115, variant: 1 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 49, y: 110 },
    // === CORRIDOR ===
    { type: 'enemy', x: 48, y: 100 },
    { type: 'enemy', x: 50, y: 90 },
    { type: 'enemy', x: 48, y: 80 },
    // === COURTYARD ===
    { type: 'enemy', x: 42, y: 60 },
    { type: 'enemy', x: 56, y: 60 },
    { type: 'enemy', x: 49, y: 56 },
    { type: 'enemy', x: 46, y: 66 },
    // === WEST CHAMBER ===
    { type: 'enemy', x: 15, y: 54 },
    { type: 'enemy', x: 19, y: 57 },
    // === EAST CHAMBER ===
    { type: 'enemy', x: 81, y: 54 },
    { type: 'enemy', x: 85, y: 57 },
    // === SECRET TOMB ===
    { type: 'enemy', x: 14, y: 88 },
    // === TEMPLE PLATFORM ===
    { type: 'enemy', x: 43, y: 38 },
    { type: 'enemy', x: 56, y: 38 },
    { type: 'enemy', x: 49, y: 34 },
    { type: 'enemy', x: 44, y: 46 },
    { type: 'enemy', x: 54, y: 46 },
    // === BOSS ===
    { type: 'boss', x: 49, y: 10 },
    // === PORTALS ===
    { type: 'portal', x: 49, y: 113 },  // South exit to swamp
    { type: 'portal', x: 49, y: 3 },    // North exit to Ergenekon
  ];

  return { biome: 'underworld', width: W, height: H, tiles, props, spawns };
}

// ============================================
// KARANLIK TUNEL - Dark Tunnel/Dungeon
// ============================================
function createKaranlikTunelTemplate(): MapTemplate {
  const W = 75, H = 50;
  const tiles = createTileGrid(W, H, TileType.VOID);

  fillCircle(tiles, 12, 40, 6, TileType.STONE);
  fillRect(tiles, 16, 38, 15, 4, TileType.STONE);
  fillRect(tiles, 28, 28, 15, 15, TileType.STONE);
  fillRect(tiles, 33, 15, 5, 14, TileType.STONE);
  fillCircle(tiles, 35, 12, 6, TileType.STONE);
  fillRect(tiles, 43, 33, 15, 4, TileType.STONE);
  fillCircle(tiles, 62, 35, 6, TileType.STONE);
  fillRect(tiles, 60, 38, 4, 10, TileType.STONE);
  fillCircle(tiles, 62, 45, 4, TileType.STONE);
  fillRect(tiles, 12, 30, 4, 10, TileType.STONE);
  fillCorridor(tiles, 14, 30, 28, 30, 4, TileType.STONE);
  fillCircle(tiles, 10, 22, 5, TileType.STONE);
  fillCircle(tiles, 10, 22, 2, TileType.LAVA);
  fillRect(tiles, 50, 10, 4, 5, TileType.STONE);
  fillCircle(tiles, 58, 10, 5, TileType.STONE);
  fillRect(tiles, 40, 12, 12, 3, TileType.STONE);

  const props: PropPlacement[] = [
    { type: 'torch', x: 8, y: 40 },
    { type: 'torch', x: 16, y: 40 },
    { type: 'sign', x: 12, y: 38 },
    { type: 'torch', x: 29, y: 29 },
    { type: 'torch', x: 41, y: 29 },
    { type: 'torch', x: 29, y: 41 },
    { type: 'torch', x: 41, y: 41 },
    { type: 'ruins', x: 35, y: 33, variant: 0 },
    { type: 'rock', x: 32, y: 38, variant: 2 },
    { type: 'rock', x: 38, y: 30, variant: 2 },
    { type: 'grave', x: 33, y: 11 },
    { type: 'grave', x: 35, y: 13 },
    { type: 'grave', x: 37, y: 10 },
    { type: 'torch', x: 31, y: 9 },
    { type: 'torch', x: 39, y: 9 },
    { type: 'crate', x: 60, y: 33 },
    { type: 'crate', x: 62, y: 35 },
    { type: 'barrel', x: 64, y: 34 },
    { type: 'barrel', x: 61, y: 37 },
    { type: 'torch', x: 58, y: 32 },
    { type: 'torch', x: 66, y: 32 },
    { type: 'rock', x: 60, y: 44, variant: 2 },
    { type: 'torch', x: 64, y: 45 },
    { type: 'torch', x: 6, y: 20 },
    { type: 'torch', x: 14, y: 20 },
    { type: 'rock', x: 7, y: 25, variant: 2 },
    { type: 'rock', x: 13, y: 24, variant: 2 },
    { type: 'ruins', x: 57, y: 9, variant: 0 },
    { type: 'statue', x: 60, y: 11, variant: 0 },
    { type: 'crate', x: 55, y: 12 },
    { type: 'torch', x: 54, y: 8 },
    { type: 'torch', x: 62, y: 8 },
    { type: 'torch', x: 22, y: 37 },
    { type: 'torch', x: 50, y: 32 },
    { type: 'torch', x: 34, y: 20 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 12, y: 40 },
    // Enemies - entrance
    { type: 'enemy', x: 14, y: 38 },
    { type: 'enemy', x: 10, y: 42 },
    // Enemies - corridor
    { type: 'enemy', x: 20, y: 38 },
    { type: 'enemy', x: 15, y: 32 },
    // Enemies - central room
    { type: 'enemy', x: 32, y: 32 },
    { type: 'enemy', x: 38, y: 35 },
    { type: 'enemy', x: 34, y: 40 },
    { type: 'enemy', x: 30, y: 28 },
    // Enemies - east chamber
    { type: 'enemy', x: 60, y: 33 },
    { type: 'enemy', x: 64, y: 37 },
    // Enemies - north graveyard
    { type: 'enemy', x: 35, y: 12 },
    { type: 'enemy', x: 33, y: 15 },
    // Enemies - NE storage
    { type: 'enemy', x: 58, y: 10 },
    { type: 'enemy', x: 56, y: 12 },
    // Enemies - lava room
    { type: 'enemy', x: 10, y: 24 },
    // Portals
    { type: 'portal', x: 8, y: 40 },    // West exit to Yer-Sub Tahti
    { type: 'portal', x: 65, y: 35 },   // East exit to Olum Nehri
  ];

  return { biome: 'underworld', width: W, height: H, tiles, props, spawns };
}

// ============================================
// TAHT ODASI - Throne Room (Boss Arena)
// ============================================
function createTahtOdasiTemplate(): MapTemplate {
  const W = 60, H = 65;
  const tiles = createTileGrid(W, H, TileType.VOID);

  fillRect(tiles, 25, 48, 10, 17, TileType.STONE);
  fillCircle(tiles, 30, 30, 22, TileType.STONE);
  fillCircle(tiles, 30, 30, 18, TileType.ROCKY_GROUND);
  fillCircle(tiles, 30, 30, 14, TileType.STONE);
  fillRect(tiles, 22, 8, 16, 10, TileType.STONE);
  fillRect(tiles, 26, 14, 8, 38, TileType.STONE);
  fillCircle(tiles, 10, 30, 5, TileType.STONE);
  fillRect(tiles, 14, 28, 14, 4, TileType.STONE);
  fillCircle(tiles, 50, 30, 5, TileType.STONE);
  fillRect(tiles, 42, 28, 10, 4, TileType.STONE);

  const props: PropPlacement[] = [
    { type: 'torch', x: 26, y: 58 },
    { type: 'torch', x: 34, y: 58 },
    { type: 'torch', x: 26, y: 52 },
    { type: 'torch', x: 34, y: 52 },
    { type: 'statue', x: 24, y: 10, variant: 1 },
    { type: 'statue', x: 36, y: 10, variant: 1 },
    { type: 'statue', x: 26, y: 14, variant: 0 },
    { type: 'statue', x: 34, y: 14, variant: 0 },
    { type: 'torch', x: 22, y: 8 },
    { type: 'torch', x: 38, y: 8 },
    { type: 'torch', x: 22, y: 25 },
    { type: 'torch', x: 38, y: 25 },
    { type: 'torch', x: 22, y: 38 },
    { type: 'torch', x: 38, y: 38 },
    { type: 'ruins', x: 25, y: 28, variant: 1 },
    { type: 'ruins', x: 35, y: 28, variant: 1 },
    { type: 'ruins', x: 28, y: 35, variant: 0 },
    { type: 'ruins', x: 32, y: 35, variant: 0 },
    { type: 'torch', x: 12, y: 15 },
    { type: 'torch', x: 48, y: 15 },
    { type: 'torch', x: 12, y: 45 },
    { type: 'torch', x: 48, y: 45 },
    { type: 'torch', x: 8, y: 30 },
    { type: 'torch', x: 52, y: 30 },
    { type: 'statue', x: 9, y: 29, variant: 0 },
    { type: 'statue', x: 51, y: 29, variant: 0 },
    { type: 'torch', x: 6, y: 28 },
    { type: 'torch', x: 54, y: 28 },
    { type: 'rock', x: 20, y: 32, variant: 2 },
    { type: 'rock', x: 40, y: 32, variant: 2 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 30, y: 60 },
    // Boss
    { type: 'boss', x: 30, y: 12 },
    // Enemies - inner arena
    { type: 'enemy', x: 22, y: 28 },
    { type: 'enemy', x: 38, y: 28 },
    { type: 'enemy', x: 25, y: 35 },
    { type: 'enemy', x: 35, y: 35 },
    { type: 'enemy', x: 30, y: 22 },
    { type: 'enemy', x: 20, y: 20 },
    { type: 'enemy', x: 40, y: 20 },
    // Enemies - side chambers
    { type: 'enemy', x: 10, y: 30 },
    { type: 'enemy', x: 50, y: 30 },
    // Enemies - corridor
    { type: 'enemy', x: 30, y: 45 },
    { type: 'enemy', x: 30, y: 52 },
    // Portals
    { type: 'portal', x: 30, y: 62 },   // South exit to cehennem_bahcesi
  ];

  return { biome: 'hell', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 2: Ergenekon Girişi - Rocky Mountain Pass (100x90)
// Winding mountain path from south to north with rocky walls, campsite, mine entrances
// ============================================
function createErgenekonGirisiTemplate(): MapTemplate {
  const W = 100, H = 90;
  const tiles = createTileGrid(W, H, TileType.MOUNTAIN);

  // === ROCKY GROUND base layer within the mountain ===
  fillRect(tiles, 5, 5, W - 10, H - 10, TileType.ROCKY_GROUND);

  // === CLIFF EDGES along mountain borders ===
  fillRect(tiles, 4, 4, W - 8, 2, TileType.CLIFF_EDGE);
  fillRect(tiles, 4, H - 6, W - 8, 2, TileType.CLIFF_EDGE);
  fillRect(tiles, 4, 4, 2, H - 8, TileType.CLIFF_EDGE);
  fillRect(tiles, W - 6, 4, 2, H - 8, TileType.CLIFF_EDGE);

  // === MAIN WINDING PATH from south to north ===
  // South entrance area
  fillCircle(tiles, 50, 85, 8, TileType.STONE);
  fillCircle(tiles, 50, 85, 5, TileType.DIRT);

  // Winding path south to center
  fillWindingPath(tiles, 50, 80, 40, 65, 4, TileType.STONE, 0.4);
  fillWindingPath(tiles, 40, 65, 55, 50, 4, TileType.STONE, 0.3);

  // === CENTRAL CAMPSITE AREA (~50, 45) ===
  fillCircle(tiles, 50, 45, 14, TileType.STONE);
  fillCircle(tiles, 50, 45, 9, TileType.DIRT);
  fillCircle(tiles, 50, 45, 5, TileType.GRASS);

  // Winding path center to north
  fillWindingPath(tiles, 50, 35, 45, 20, 4, TileType.STONE, 0.3);
  fillWindingPath(tiles, 45, 20, 50, 8, 4, TileType.STONE, 0.2);

  // North exit clearing
  fillCircle(tiles, 50, 5, 7, TileType.STONE);
  fillCircle(tiles, 50, 5, 4, TileType.DIRT);

  // === MOUNTAIN WALLS on both sides of the path ===
  // West mountain ridge
  fillEllipse(tiles, 15, 30, 10, 20, TileType.MOUNTAIN);
  fillEllipse(tiles, 12, 65, 8, 12, TileType.MOUNTAIN);
  // East mountain ridge
  fillEllipse(tiles, 85, 35, 10, 18, TileType.MOUNTAIN);
  fillEllipse(tiles, 88, 70, 8, 10, TileType.MOUNTAIN);

  // === GRASS PATCHES between rocks ===
  fillCircle(tiles, 30, 25, 5, TileType.GRASS);
  fillCircle(tiles, 70, 55, 4, TileType.GRASS);
  fillCircle(tiles, 25, 70, 3, TileType.GRASS);
  fillCircle(tiles, 75, 30, 4, TileType.GRASS);

  // === SIDE PATHS ===
  // West alcove (mine entrance area)
  fillWindingPath(tiles, 42, 45, 28, 50, 3, TileType.STONE, 0.2);
  fillCircle(tiles, 25, 50, 6, TileType.STONE);
  fillCircle(tiles, 25, 50, 3, TileType.ROCKY_GROUND);

  // East lookout
  fillWindingPath(tiles, 58, 45, 72, 40, 3, TileType.STONE, 0.2);
  fillCircle(tiles, 75, 38, 6, TileType.STONE);
  fillCircle(tiles, 75, 38, 3, TileType.DIRT);

  // === SMALL STREAM (west side) ===
  fillWindingPath(tiles, 20, 15, 30, 35, 2, TileType.SHALLOW_WATER, 0.3);
  fillWindingPath(tiles, 30, 35, 35, 55, 1, TileType.SHALLOW_WATER, 0.2);

  const props: PropPlacement[] = [
    // === SOUTH ENTRANCE ===
    { type: 'sign', x: 53, y: 83 },
    { type: 'torch', x: 44, y: 85 },
    { type: 'torch', x: 56, y: 85 },
    // === PATH TORCHES ===
    { type: 'torch', x: 42, y: 70 },
    { type: 'torch', x: 52, y: 60 },
    { type: 'torch', x: 46, y: 50 },
    { type: 'torch', x: 48, y: 30 },
    { type: 'torch', x: 46, y: 15 },
    // === CENTRAL CAMPSITE ===
    { type: 'campfire', x: 50, y: 45 },
    { type: 'tent', x: 44, y: 42, variant: 0 },
    { type: 'tent', x: 56, y: 42, variant: 1 },
    { type: 'tent', x: 50, y: 38, variant: 2 },
    { type: 'barrel', x: 42, y: 48 },
    { type: 'crate', x: 58, y: 48 },
    { type: 'cart', x: 55, y: 52 },
    { type: 'torch', x: 38, y: 40 },
    { type: 'torch', x: 62, y: 40 },
    { type: 'torch', x: 38, y: 50 },
    { type: 'torch', x: 62, y: 50 },
    // === MINE ENTRANCES (west alcove) ===
    { type: 'stairs_down', x: 24, y: 48 },
    { type: 'stairs_down', x: 27, y: 52 },
    { type: 'torch', x: 22, y: 47 },
    { type: 'torch', x: 29, y: 53 },
    // === EAST LOOKOUT ===
    { type: 'campfire', x: 75, y: 38 },
    { type: 'rock', x: 78, y: 35, variant: 0 },
    { type: 'rock', x: 72, y: 41, variant: 1 },
    // === ROCKS scattered along mountain walls ===
    { type: 'rock', x: 18, y: 22, variant: 0 },
    { type: 'rock', x: 22, y: 28, variant: 1 },
    { type: 'rock', x: 80, y: 45, variant: 2 },
    { type: 'rock', x: 82, y: 55, variant: 0 },
    { type: 'rock', x: 15, y: 55, variant: 1 },
    { type: 'rock', x: 35, y: 15, variant: 2 },
    { type: 'rock', x: 60, y: 20, variant: 0 },
    { type: 'rock', x: 68, y: 65, variant: 1 },
    { type: 'rock', x: 32, y: 75, variant: 2 },
    { type: 'rock', x: 65, y: 78, variant: 0 },
    // === CLIFF props ===
    { type: 'cliff', x: 10, y: 40 },
    { type: 'cliff', x: 90, y: 50 },
    { type: 'cliff', x: 15, y: 75 },
    { type: 'cliff', x: 85, y: 25 },
    // === NORTH EXIT ===
    { type: 'sign', x: 53, y: 3 },
    { type: 'torch', x: 45, y: 5 },
    { type: 'torch', x: 55, y: 5 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 50, y: 85 },
    // === South entrance guards ===
    { type: 'enemy', x: 45, y: 78 },
    { type: 'enemy', x: 55, y: 78 },
    // === Path encounters ===
    { type: 'enemy', x: 42, y: 68 },
    { type: 'enemy', x: 48, y: 62 },
    { type: 'enemy', x: 52, y: 55 },
    // === Central campsite (Demir Kurt packs) ===
    { type: 'enemy', x: 40, y: 44 },
    { type: 'enemy', x: 60, y: 44 },
    { type: 'enemy', x: 50, y: 52 },
    // === Mine entrance (Kaya Golemi) ===
    { type: 'enemy', x: 24, y: 50 },
    { type: 'enemy', x: 28, y: 48 },
    // === East lookout ===
    { type: 'enemy', x: 73, y: 40 },
    { type: 'enemy', x: 77, y: 36 },
    // === North path ===
    { type: 'enemy', x: 47, y: 25 },
    { type: 'enemy', x: 44, y: 18 },
    { type: 'enemy', x: 48, y: 12 },
    // === Grass patches (scattered) ===
    { type: 'enemy', x: 30, y: 23 },
    { type: 'enemy', x: 72, y: 56 },
    // === PORTALS ===
    { type: 'portal', x: 50, y: 87 },   // South exit back to Act 1
    { type: 'portal', x: 50, y: 3 },    // North exit to Firtina Ovasi
  ];

  return { biome: 'mountains', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 2: Fırtına Ovası - Storm Steppe (120x100)
// Open steppe with abandoned nomad camp, dried riverbed, rolling hills, ruins
// ============================================
function createFirtinaOvasiTemplate(): MapTemplate {
  const W = 120, H = 100;
  const tiles = createTileGrid(W, H, TileType.GRASS);

  // === BORDER - darker grass edges ===
  fillRect(tiles, 0, 0, W, 4, TileType.DARK_GRASS);
  fillRect(tiles, 0, H - 4, W, 4, TileType.DARK_GRASS);
  fillRect(tiles, 0, 0, 4, H, TileType.DARK_GRASS);
  fillRect(tiles, W - 4, 0, 4, H, TileType.DARK_GRASS);

  // === ROLLING HILLS scattered ===
  fillCircle(tiles, 25, 20, 8, TileType.HILL);
  fillCircle(tiles, 80, 15, 6, TileType.HILL);
  fillCircle(tiles, 95, 60, 7, TileType.HILL);
  fillCircle(tiles, 15, 75, 5, TileType.HILL);
  fillCircle(tiles, 55, 80, 6, TileType.HILL);
  fillCircle(tiles, 105, 85, 5, TileType.HILL);

  // === DIRT ROADS connecting west to east ===
  // Main east-west road
  fillWindingPath(tiles, 5, 50, 60, 48, 4, TileType.DIRT, 0.2);
  fillWindingPath(tiles, 60, 48, 115, 50, 4, TileType.DIRT, 0.2);
  // North-south cross road
  fillWindingPath(tiles, 60, 20, 60, 80, 3, TileType.DIRT, 0.15);

  // === WEST PORTAL AREA (tile 3, 50) ===
  fillCircle(tiles, 5, 50, 6, TileType.DIRT);
  fillCircle(tiles, 5, 50, 3, TileType.STONE);

  // === EAST PORTAL AREA (tile 117, 50) ===
  fillCircle(tiles, 115, 50, 6, TileType.DIRT);
  fillCircle(tiles, 115, 50, 3, TileType.STONE);

  // === DRIED RIVERBED (winding SAND path NW to SE) ===
  fillWindingPath(tiles, 10, 10, 35, 35, 3, TileType.SAND, 0.4);
  fillWindingPath(tiles, 35, 35, 70, 60, 3, TileType.SAND, 0.3);
  fillWindingPath(tiles, 70, 60, 110, 90, 3, TileType.SAND, 0.4);

  // === ABANDONED NOMAD CAMP (center, ~60, 48) ===
  fillCircle(tiles, 60, 48, 12, TileType.DIRT);
  fillCircle(tiles, 60, 48, 7, TileType.SAND);

  // === SAND PATCHES ===
  fillCircle(tiles, 40, 30, 5, TileType.SAND);
  fillCircle(tiles, 90, 40, 4, TileType.SAND);
  fillCircle(tiles, 30, 65, 3, TileType.SAND);

  // === STONE RUINS (scattered) ===
  // NE ruins
  fillRect(tiles, 88, 22, 12, 10, TileType.STONE);
  fillRect(tiles, 90, 24, 8, 6, TileType.ROCKY_GROUND);
  // SW ruins
  fillRect(tiles, 18, 58, 10, 8, TileType.STONE);
  fillRect(tiles, 20, 60, 6, 4, TileType.ROCKY_GROUND);
  // SE ruins
  fillCircle(tiles, 100, 75, 6, TileType.STONE);
  fillCircle(tiles, 100, 75, 3, TileType.ROCKY_GROUND);

  const props: PropPlacement[] = [
    // === WEST PORTAL ===
    { type: 'sign', x: 8, y: 48 },
    { type: 'torch', x: 3, y: 46 },
    { type: 'torch', x: 3, y: 54 },
    // === EAST PORTAL ===
    { type: 'sign', x: 112, y: 48 },
    { type: 'torch', x: 117, y: 46 },
    { type: 'torch', x: 117, y: 54 },
    // === ABANDONED NOMAD CAMP ===
    { type: 'tent', x: 55, y: 45, variant: 0 },
    { type: 'tent', x: 65, y: 45, variant: 1 },
    { type: 'tent', x: 58, y: 52, variant: 2 },
    { type: 'tent', x: 63, y: 52, variant: 0 },
    { type: 'cart', x: 52, y: 48 },
    { type: 'cart', x: 68, y: 48 },
    { type: 'barrel', x: 54, y: 44 },
    { type: 'barrel', x: 66, y: 44 },
    { type: 'crate', x: 56, y: 54 },
    { type: 'crate', x: 64, y: 54 },
    { type: 'campfire', x: 60, y: 48 },
    // === NE RUINS ===
    { type: 'ruins', x: 90, y: 24, variant: 0 },
    { type: 'ruins', x: 96, y: 28, variant: 1 },
    { type: 'rock', x: 88, y: 28, variant: 0 },
    { type: 'torch', x: 87, y: 22 },
    { type: 'torch', x: 100, y: 22 },
    // === SW RUINS ===
    { type: 'ruins', x: 20, y: 60, variant: 0 },
    { type: 'ruins', x: 24, y: 62, variant: 1 },
    { type: 'rock', x: 18, y: 64, variant: 1 },
    // === SE RUINS ===
    { type: 'ruins', x: 98, y: 73, variant: 0 },
    { type: 'ruins', x: 102, y: 77, variant: 1 },
    { type: 'rock', x: 96, y: 78, variant: 2 },
    // === BUSHES scattered across the steppe ===
    { type: 'bush', x: 20, y: 40, variant: 0 },
    { type: 'bush', x: 35, y: 55, variant: 1 },
    { type: 'bush', x: 45, y: 25, variant: 0 },
    { type: 'bush', x: 75, y: 35, variant: 1 },
    { type: 'bush', x: 85, y: 55, variant: 0 },
    { type: 'bush', x: 50, y: 70, variant: 1 },
    { type: 'bush', x: 40, y: 85, variant: 0 },
    { type: 'bush', x: 80, y: 80, variant: 1 },
    { type: 'bush', x: 15, y: 30, variant: 0 },
    { type: 'bush', x: 105, y: 30, variant: 1 },
    { type: 'bush', x: 70, y: 70, variant: 0 },
    { type: 'bush', x: 30, y: 15, variant: 1 },
    // === ROCKS scattered ===
    { type: 'rock', x: 30, y: 45, variant: 0 },
    { type: 'rock', x: 85, y: 48, variant: 1 },
    { type: 'rock', x: 50, y: 35, variant: 2 },
    { type: 'rock', x: 70, y: 25, variant: 0 },
    { type: 'rock', x: 40, y: 75, variant: 1 },
    { type: 'rock', x: 95, y: 55, variant: 2 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 5, y: 50 },
    // === West approach (easy) ===
    { type: 'enemy', x: 15, y: 48 },
    { type: 'enemy', x: 20, y: 52 },
    // === Road encounters ===
    { type: 'enemy', x: 30, y: 50 },
    { type: 'enemy', x: 42, y: 48 },
    { type: 'enemy', x: 78, y: 50 },
    { type: 'enemy', x: 90, y: 48 },
    // === Central camp (Ruzgar Ruhu spread out) ===
    { type: 'enemy', x: 55, y: 44 },
    { type: 'enemy', x: 65, y: 44 },
    { type: 'enemy', x: 58, y: 54 },
    { type: 'enemy', x: 62, y: 54 },
    // === NE ruins (Ates Iblisi) ===
    { type: 'enemy', x: 90, y: 25 },
    { type: 'enemy', x: 95, y: 28 },
    { type: 'enemy', x: 92, y: 30 },
    // === SW ruins ===
    { type: 'enemy', x: 20, y: 61 },
    { type: 'enemy', x: 25, y: 63 },
    // === SE ruins ===
    { type: 'enemy', x: 99, y: 74 },
    { type: 'enemy', x: 103, y: 77 },
    // === Hills (scattered patrols) ===
    { type: 'enemy', x: 25, y: 20 },
    { type: 'enemy', x: 80, y: 16 },
    { type: 'enemy', x: 55, y: 82 },
    { type: 'enemy', x: 15, y: 76 },
    // === PORTALS ===
    { type: 'portal', x: 3, y: 50 },    // West exit back to Ergenekon Girisi
    { type: 'portal', x: 117, y: 50 },   // East exit to Ates Magarasi
  ];

  return { biome: 'desert', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 2: Ateş Mağarası - Volcanic Cave (90x85)
// Lava rivers, stone island paths, bridge crossings, stalactite clusters
// ============================================
function createAtesMagarasiTemplate(): MapTemplate {
  const W = 90, H = 85;
  const tiles = createTileGrid(W, H, TileType.STONE);

  // === VOID borders (cave walls) ===
  fillRect(tiles, 0, 0, W, 3, TileType.VOID);
  fillRect(tiles, 0, H - 3, W, 3, TileType.VOID);
  fillRect(tiles, 0, 0, 3, H, TileType.VOID);
  fillRect(tiles, W - 3, 0, 3, H, TileType.VOID);

  // === ROCKY GROUND base in cave interior ===
  fillRect(tiles, 3, 3, W - 6, H - 6, TileType.ROCKY_GROUND);

  // === LAVA RIVERS ===
  // Main horizontal lava river (splits cave north/south)
  fillRect(tiles, 8, 38, W - 16, 5, TileType.LAVA);
  // Vertical lava river (splits cave east/west)
  fillRect(tiles, 42, 8, 5, H - 16, TileType.LAVA);
  // Diagonal lava tributary (NW to center)
  fillWindingPath(tiles, 10, 10, 42, 38, 3, TileType.LAVA, 0.2);
  // Diagonal lava tributary (SE from center)
  fillWindingPath(tiles, 47, 43, 80, 75, 3, TileType.LAVA, 0.2);

  // === STONE ISLAND PATHS (safe walkable areas) ===
  // West entrance island
  fillCircle(tiles, 8, 42, 8, TileType.STONE);
  fillCircle(tiles, 8, 42, 5, TileType.ROCKY_GROUND);

  // NW safe zone
  fillCircle(tiles, 22, 22, 10, TileType.STONE);
  fillCircle(tiles, 22, 22, 6, TileType.ROCKY_GROUND);

  // NE safe zone
  fillCircle(tiles, 68, 20, 9, TileType.STONE);
  fillCircle(tiles, 68, 20, 5, TileType.ROCKY_GROUND);

  // Center platform (large, main fighting area)
  fillCircle(tiles, 45, 42, 14, TileType.STONE);
  fillCircle(tiles, 45, 42, 9, TileType.ROCKY_GROUND);
  fillCircle(tiles, 45, 42, 4, TileType.DIRT);

  // SW safe zone
  fillCircle(tiles, 22, 65, 8, TileType.STONE);
  fillCircle(tiles, 22, 65, 5, TileType.ROCKY_GROUND);

  // SE safe zone (exit area)
  fillCircle(tiles, 75, 60, 9, TileType.STONE);
  fillCircle(tiles, 75, 60, 5, TileType.ROCKY_GROUND);

  // East exit clearing
  fillCircle(tiles, 85, 42, 6, TileType.STONE);
  fillCircle(tiles, 85, 42, 3, TileType.DIRT);

  // === BRIDGES over lava ===
  // West bridge (entrance to center)
  fillRect(tiles, 14, 40, 18, 3, TileType.STONE);
  // North bridge (NW to NE over vertical lava)
  fillRect(tiles, 40, 20, 7, 3, TileType.STONE);
  // South bridge (SW to center over vertical lava)
  fillRect(tiles, 40, 62, 7, 3, TileType.STONE);
  // East bridge (center to SE)
  fillRect(tiles, 56, 40, 14, 3, TileType.STONE);
  // Far east bridge (SE to exit)
  fillRect(tiles, 80, 42, 6, 3, TileType.STONE);
  // NW to center bridge (over horizontal lava)
  fillRect(tiles, 22, 30, 3, 10, TileType.STONE);
  // SW connector
  fillRect(tiles, 22, 45, 3, 18, TileType.STONE);

  // === DARK GRASS rare patches (cave moss) ===
  fillCircle(tiles, 20, 25, 3, TileType.DARK_GRASS);
  fillCircle(tiles, 70, 18, 2, TileType.DARK_GRASS);
  fillCircle(tiles, 25, 68, 2, TileType.DARK_GRASS);

  // === CLIFF EDGES around lava for visual border ===
  for (let y = 4; y < H - 4; y++) {
    for (let x = 4; x < W - 4; x++) {
      if (tiles[y][x] === TileType.LAVA) {
        for (const [dx, dy] of [[1,0],[-1,0],[0,1],[0,-1]]) {
          const nx = x + dx, ny = y + dy;
          if (ny >= 0 && ny < H && nx >= 0 && nx < W &&
              tiles[ny][nx] !== TileType.LAVA &&
              tiles[ny][nx] !== TileType.STONE &&
              tiles[ny][nx] !== TileType.DIRT &&
              tiles[ny][nx] !== TileType.DARK_GRASS &&
              tiles[ny][nx] !== TileType.VOID) {
            tiles[ny][nx] = TileType.CLIFF_EDGE;
          }
        }
      }
    }
  }

  const props: PropPlacement[] = [
    // === WEST ENTRANCE ===
    { type: 'torch', x: 5, y: 40 },
    { type: 'torch', x: 5, y: 44 },
    { type: 'sign', x: 8, y: 38 },
    // === BRIDGES (torches at endpoints) ===
    { type: 'torch', x: 14, y: 39 },
    { type: 'torch', x: 30, y: 39 },
    { type: 'torch', x: 56, y: 39 },
    { type: 'torch', x: 68, y: 39 },
    { type: 'torch', x: 80, y: 41 },
    // === STALACTITE/STALAGMITE clusters (pillars) ===
    { type: 'pillar', x: 18, y: 20 },
    { type: 'pillar', x: 26, y: 18 },
    { type: 'pillar', x: 65, y: 16 },
    { type: 'pillar', x: 72, y: 22 },
    { type: 'pillar', x: 18, y: 62 },
    { type: 'pillar', x: 26, y: 68 },
    { type: 'pillar', x: 72, y: 58 },
    { type: 'pillar', x: 78, y: 64 },
    // === CENTER PLATFORM ===
    { type: 'pillar', x: 38, y: 38 },
    { type: 'pillar', x: 52, y: 38 },
    { type: 'pillar', x: 38, y: 48 },
    { type: 'pillar', x: 52, y: 48 },
    { type: 'torch', x: 40, y: 36 },
    { type: 'torch', x: 50, y: 36 },
    { type: 'torch', x: 40, y: 48 },
    { type: 'torch', x: 50, y: 48 },
    // === GLOWING CRYSTALS (torches placed differently) ===
    { type: 'torch', x: 20, y: 24 },
    { type: 'torch', x: 70, y: 20 },
    { type: 'torch', x: 24, y: 66 },
    { type: 'torch', x: 76, y: 58 },
    // === ROCKS ===
    { type: 'rock', x: 15, y: 28, variant: 0 },
    { type: 'rock', x: 28, y: 15, variant: 1 },
    { type: 'rock', x: 62, y: 25, variant: 2 },
    { type: 'rock', x: 30, y: 60, variant: 0 },
    { type: 'rock', x: 80, y: 55, variant: 1 },
    { type: 'rock', x: 50, y: 70, variant: 2 },
    // === STAIRS (mine shafts) ===
    { type: 'stairs', x: 68, y: 15 },
    { type: 'stairs', x: 22, y: 70 },
    // === EAST EXIT ===
    { type: 'sign', x: 83, y: 40 },
    { type: 'torch', x: 87, y: 40 },
    { type: 'torch', x: 87, y: 44 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 8, y: 42 },
    // === West entrance ===
    { type: 'enemy', x: 12, y: 40 },
    { type: 'enemy', x: 12, y: 44 },
    // === NW zone (Ates Iblisi near lava) ===
    { type: 'enemy', x: 20, y: 20 },
    { type: 'enemy', x: 24, y: 24 },
    { type: 'enemy', x: 18, y: 26 },
    // === NE zone ===
    { type: 'enemy', x: 66, y: 18 },
    { type: 'enemy', x: 70, y: 22 },
    // === Center platform (main fight area) ===
    { type: 'enemy', x: 42, y: 40 },
    { type: 'enemy', x: 48, y: 40 },
    { type: 'enemy', x: 45, y: 46 },
    { type: 'enemy', x: 42, y: 36 },
    // === SW zone ===
    { type: 'enemy', x: 20, y: 64 },
    { type: 'enemy', x: 24, y: 68 },
    // === SE zone (Dag Devi in wide area) ===
    { type: 'enemy', x: 73, y: 58 },
    { type: 'enemy', x: 77, y: 62 },
    { type: 'enemy', x: 75, y: 55 },
    // === Bridge ambushes ===
    { type: 'enemy', x: 22, y: 41 },
    { type: 'enemy', x: 62, y: 41 },
    // === PORTALS ===
    { type: 'portal', x: 3, y: 42 },    // West exit back to Firtina Ovasi
    { type: 'portal', x: 87, y: 42 },   // East exit to Yer-Sub Tahti
  ];

  return { biome: 'desert', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 2: Yer-Sub Tahtı - Underground Palace (80x100)
// Grand entrance corridor, central throne room, side chambers, decorative pools, void abyss
// ============================================
function createYersubTahtiTemplate(): MapTemplate {
  const W = 80, H = 100;
  const tiles = createTileGrid(W, H, TileType.VOID);

  // === SOUTH ENTRANCE CORRIDOR (y: 85-97) ===
  fillRect(tiles, 33, 85, 14, 12, TileType.STONE);
  fillRect(tiles, 35, 87, 10, 10, TileType.ROCKY_GROUND);

  // === GRAND ENTRANCE HALL (y: 70-85) ===
  fillRect(tiles, 28, 70, 24, 16, TileType.STONE);
  fillRect(tiles, 30, 72, 20, 12, TileType.ROCKY_GROUND);

  // === MAIN CORRIDOR from entrance to throne (wide, pillar-lined) ===
  fillRect(tiles, 33, 35, 14, 36, TileType.STONE);
  fillRect(tiles, 35, 37, 10, 32, TileType.ROCKY_GROUND);

  // === CENTRAL THRONE ROOM (~40, 25, large ~30x30 open area) ===
  fillRect(tiles, 10, 10, 60, 30, TileType.STONE);
  fillRect(tiles, 14, 14, 52, 22, TileType.ROCKY_GROUND);
  // Inner floor (polished stone appearance)
  fillRect(tiles, 20, 18, 40, 14, TileType.STONE);

  // === THRONE PLATFORM (north center) ===
  fillRect(tiles, 32, 6, 16, 8, TileType.STONE);
  fillRect(tiles, 34, 8, 12, 4, TileType.DIRT);

  // === DECORATIVE POOLS flanking throne ===
  // West pool
  fillRect(tiles, 16, 16, 8, 10, TileType.DEEP_WATER);
  fillRect(tiles, 14, 14, 12, 14, TileType.STONE);
  fillRect(tiles, 16, 16, 8, 10, TileType.DEEP_WATER);
  // East pool
  fillRect(tiles, 56, 16, 8, 10, TileType.DEEP_WATER);
  fillRect(tiles, 54, 14, 12, 14, TileType.STONE);
  fillRect(tiles, 56, 16, 8, 10, TileType.DEEP_WATER);

  // === WEST SIDE CHAMBER (treasure room) ===
  fillRect(tiles, 5, 45, 18, 16, TileType.STONE);
  fillRect(tiles, 7, 47, 14, 12, TileType.ROCKY_GROUND);
  // Corridor from main hall to west chamber
  fillRect(tiles, 22, 50, 12, 4, TileType.STONE);

  // === EAST SIDE CHAMBER (elite guard room) ===
  fillRect(tiles, 57, 45, 18, 16, TileType.STONE);
  fillRect(tiles, 59, 47, 14, 12, TileType.ROCKY_GROUND);
  // Corridor from main hall to east chamber
  fillRect(tiles, 46, 50, 12, 4, TileType.STONE);

  // === DARK GRASS (moss) patches ===
  fillCircle(tiles, 12, 50, 3, TileType.DARK_GRASS);
  fillCircle(tiles, 68, 50, 3, TileType.DARK_GRASS);
  fillCircle(tiles, 40, 78, 2, TileType.DARK_GRASS);

  // === CONNECTING CORRIDORS from throne room to side areas ===
  // West corridor from throne room
  fillRect(tiles, 10, 35, 4, 12, TileType.STONE);
  // East corridor from throne room
  fillRect(tiles, 66, 35, 4, 12, TileType.STONE);

  const props: PropPlacement[] = [
    // === SOUTH ENTRANCE ===
    { type: 'torch', x: 33, y: 94 },
    { type: 'torch', x: 47, y: 94 },
    { type: 'sign', x: 40, y: 92 },
    // === ENTRANCE HALL ===
    { type: 'pillar', x: 30, y: 72 },
    { type: 'pillar', x: 50, y: 72 },
    { type: 'pillar', x: 30, y: 82 },
    { type: 'pillar', x: 50, y: 82 },
    { type: 'torch', x: 28, y: 70 },
    { type: 'torch', x: 52, y: 70 },
    // === MAIN CORRIDOR PILLARS (every ~5 tiles) ===
    { type: 'pillar', x: 33, y: 40 },
    { type: 'pillar', x: 47, y: 40 },
    { type: 'pillar', x: 33, y: 48 },
    { type: 'pillar', x: 47, y: 48 },
    { type: 'pillar', x: 33, y: 56 },
    { type: 'pillar', x: 47, y: 56 },
    { type: 'pillar', x: 33, y: 64 },
    { type: 'pillar', x: 47, y: 64 },
    // Corridor torches
    { type: 'torch', x: 34, y: 44 },
    { type: 'torch', x: 46, y: 44 },
    { type: 'torch', x: 34, y: 52 },
    { type: 'torch', x: 46, y: 52 },
    { type: 'torch', x: 34, y: 60 },
    { type: 'torch', x: 46, y: 60 },
    { type: 'torch', x: 34, y: 68 },
    { type: 'torch', x: 46, y: 68 },
    // === THRONE ROOM ===
    // Pillars lining the throne room
    { type: 'pillar', x: 16, y: 12 },
    { type: 'pillar', x: 64, y: 12 },
    { type: 'pillar', x: 16, y: 32 },
    { type: 'pillar', x: 64, y: 32 },
    { type: 'pillar', x: 28, y: 12 },
    { type: 'pillar', x: 52, y: 12 },
    { type: 'pillar', x: 28, y: 32 },
    { type: 'pillar', x: 52, y: 32 },
    // Throne area statues
    { type: 'statue', x: 36, y: 8, variant: 0 },
    { type: 'statue', x: 44, y: 8, variant: 0 },
    { type: 'statue', x: 32, y: 12, variant: 1 },
    { type: 'statue', x: 48, y: 12, variant: 1 },
    // Throne room torches
    { type: 'torch', x: 12, y: 14 },
    { type: 'torch', x: 68, y: 14 },
    { type: 'torch', x: 12, y: 28 },
    { type: 'torch', x: 68, y: 28 },
    { type: 'torch', x: 22, y: 18 },
    { type: 'torch', x: 58, y: 18 },
    { type: 'torch', x: 22, y: 28 },
    { type: 'torch', x: 58, y: 28 },
    { type: 'torch', x: 34, y: 6 },
    { type: 'torch', x: 46, y: 6 },
    // Ruins decorations
    { type: 'ruins', x: 20, y: 22, variant: 0 },
    { type: 'ruins', x: 60, y: 22, variant: 1 },
    // === WEST SIDE CHAMBER (treasure) ===
    { type: 'crate', x: 8, y: 48 },
    { type: 'crate', x: 12, y: 48 },
    { type: 'barrel', x: 10, y: 56 },
    { type: 'barrel', x: 16, y: 56 },
    { type: 'torch', x: 6, y: 46 },
    { type: 'torch', x: 20, y: 46 },
    { type: 'torch', x: 6, y: 58 },
    { type: 'torch', x: 20, y: 58 },
    // === EAST SIDE CHAMBER (elite guards) ===
    { type: 'ruins', x: 62, y: 48, variant: 0 },
    { type: 'ruins', x: 68, y: 54, variant: 1 },
    { type: 'torch', x: 58, y: 46 },
    { type: 'torch', x: 72, y: 46 },
    { type: 'torch', x: 58, y: 58 },
    { type: 'torch', x: 72, y: 58 },
    // === STAIRS (connecting levels) ===
    { type: 'stairs', x: 40, y: 35 },
  ];

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 40, y: 95 },
    // === Entrance hall ===
    { type: 'enemy', x: 35, y: 74 },
    { type: 'enemy', x: 45, y: 74 },
    // === Main corridor ===
    { type: 'enemy', x: 38, y: 65 },
    { type: 'enemy', x: 42, y: 58 },
    { type: 'enemy', x: 38, y: 50 },
    { type: 'enemy', x: 42, y: 42 },
    // === West side chamber (Elite Dag Devi) ===
    { type: 'enemy', x: 12, y: 52 },
    { type: 'enemy', x: 16, y: 52 },
    // === East side chamber (Elite Dag Devi) ===
    { type: 'enemy', x: 64, y: 52 },
    { type: 'enemy', x: 68, y: 52 },
    // === Throne room guards ===
    { type: 'enemy', x: 20, y: 20 },
    { type: 'enemy', x: 60, y: 20 },
    { type: 'enemy', x: 30, y: 28 },
    { type: 'enemy', x: 50, y: 28 },
    // === BOSS at throne ===
    { type: 'boss', x: 40, y: 10 },
    // === PORTALS ===
    { type: 'portal', x: 40, y: 97 },   // South exit back to Ates Magarasi
    { type: 'portal', x: 40, y: 3 },    // North (throne area) to Act 3
  ];

  return { biome: 'underworld', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 3: Ölüm Nehri - River of Death
// ============================================
function createOlumNehriTemplate(): MapTemplate {
  const W = 50, H = 60;
  const tiles = createTileGrid(W, H, TileType.DARK_GRASS);
  const props: PropPlacement[] = [];

  // Void borders
  fillRect(tiles, 0, 0, W, 2, TileType.VOID);
  fillRect(tiles, 0, H - 2, W, 2, TileType.VOID);
  fillRect(tiles, 0, 0, 2, H, TileType.VOID);
  fillRect(tiles, W - 2, 0, 2, H, TileType.VOID);

  // The River of Death - winding deep water through center
  const riverX = W / 2;
  for (let y = 3; y < H - 3; y++) {
    const offset = Math.floor(Math.sin(y * 0.15) * 6);
    fillRect(tiles, riverX + offset - 3, y, 7, 1, TileType.DEEP_WATER);
    // Swamp edges
    if (riverX + offset - 5 >= 0) tiles[y][riverX + offset - 4] = TileType.SWAMP_WATER;
    if (riverX + offset - 5 >= 0) tiles[y][riverX + offset - 5] = TileType.MUD;
    if (riverX + offset + 4 < W) tiles[y][riverX + offset + 4] = TileType.SWAMP_WATER;
    if (riverX + offset + 5 < W) tiles[y][riverX + offset + 5] = TileType.MUD;
  }

  // Bridges across river
  fillRect(tiles, riverX - 6, 15, 13, 2, TileType.STONE);
  fillRect(tiles, riverX - 6, H / 2, 13, 2, TileType.STONE);
  fillRect(tiles, riverX - 6, H - 18, 13, 2, TileType.STONE);

  // Stone clearings on both sides
  fillCircle(tiles, 10, 15, 5, TileType.STONE);
  fillCircle(tiles, W - 10, H / 2, 6, TileType.STONE);
  fillCircle(tiles, 12, H - 15, 4, TileType.STONE);

  // Paths along the river banks
  fillCorridor(tiles, 8, 5, 8, H - 5, 3, TileType.DIRT);
  fillCorridor(tiles, W - 8, 5, W - 8, H - 5, 3, TileType.DIRT);

  // Props - graves, ruins
  for (let i = 0; i < 5; i++) {
    props.push({ type: 'grave', x: 5 + Math.floor(Math.random() * 12), y: 5 + Math.floor(Math.random() * (H - 10)) });
  }
  for (let i = 0; i < 4; i++) {
    props.push({ type: 'ruins', x: W - 15 + Math.floor(Math.random() * 10), y: 5 + Math.floor(Math.random() * (H - 10)) });
  }
  props.push({ type: 'torch', x: riverX - 7, y: 15 });
  props.push({ type: 'torch', x: riverX + 7, y: 15 });

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 8, y: 5 },
    // Enemies - west bank
    { type: 'enemy', x: 8, y: 10 },
    { type: 'enemy', x: 10, y: 18 },
    { type: 'enemy', x: 8, y: 25 },
    { type: 'enemy', x: 12, y: 35 },
    { type: 'enemy', x: 8, y: 42 },
    { type: 'enemy', x: 10, y: 50 },
    // Enemies - east bank
    { type: 'enemy', x: 42, y: 10 },
    { type: 'enemy', x: 40, y: 22 },
    { type: 'enemy', x: 42, y: 35 },
    { type: 'enemy', x: 40, y: 45 },
    { type: 'enemy', x: 42, y: 52 },
    // Enemies - bridges
    { type: 'enemy', x: 25, y: 15 },
    { type: 'enemy', x: 25, y: 30 },
    { type: 'enemy', x: 25, y: 42 },
    // Portals
    { type: 'portal', x: 5, y: 30 },    // West exit to Karanlik Tunel
    { type: 'portal', x: 45, y: 30 },   // East exit to Kemik Ormani
  ];

  return { biome: 'underworld', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 3: Kemik Ormanı - Bone Forest
// ============================================
function createKemikOrmaniTemplate(): MapTemplate {
  const W = 55, H = 55;
  const tiles = createTileGrid(W, H, TileType.DARK_GRASS);
  const props: PropPlacement[] = [];

  // Void borders
  fillRect(tiles, 0, 0, W, 2, TileType.VOID);
  fillRect(tiles, 0, H - 2, W, 2, TileType.VOID);
  fillRect(tiles, 0, 0, 2, H, TileType.VOID);
  fillRect(tiles, W - 2, 0, 2, H, TileType.VOID);

  // Bone-white ground patches (stone)
  for (let i = 0; i < 12; i++) {
    const cx = 5 + Math.floor(Math.random() * (W - 10));
    const cy = 5 + Math.floor(Math.random() * (H - 10));
    fillCircle(tiles, cx, cy, 2 + Math.floor(Math.random() * 3), TileType.STONE);
  }

  // Central clearing - boss arena
  fillCircle(tiles, W / 2, H / 2, 10, TileType.DIRT);
  fillCircle(tiles, W / 2, H / 2, 6, TileType.STONE);

  // Winding paths connecting edges
  fillCorridor(tiles, 4, H / 2, W / 2, H / 2, 3, TileType.DIRT);
  fillCorridor(tiles, W / 2, H / 2, W - 4, H / 2, 3, TileType.DIRT);
  fillCorridor(tiles, W / 2, 4, W / 2, H / 2, 3, TileType.DIRT);
  fillCorridor(tiles, W / 2, H / 2, W / 2, H - 4, 3, TileType.DIRT);

  // Dead tree forest (dense)
  for (let i = 0; i < 25; i++) {
    const tx = 4 + Math.floor(Math.random() * (W - 8));
    const ty = 4 + Math.floor(Math.random() * (H - 8));
    // Don't place on paths/clearings
    if (tiles[ty] && tiles[ty][tx] === TileType.DARK_GRASS) {
      props.push({ type: 'tree_dead', x: tx, y: ty, variant: i % 2 });
    }
  }

  // Graves scattered around
  for (let i = 0; i < 8; i++) {
    props.push({ type: 'grave', x: 4 + Math.floor(Math.random() * (W - 8)), y: 4 + Math.floor(Math.random() * (H - 8)) });
  }

  // Ruins
  props.push({ type: 'ruins', x: W / 2 - 3, y: H / 2 - 3 });
  props.push({ type: 'ruins', x: W / 2 + 3, y: H / 2 + 3 });

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 4, y: 27 },
    // Enemies - west corridor
    { type: 'enemy', x: 8, y: 27 },
    { type: 'enemy', x: 14, y: 27 },
    // Enemies - east corridor
    { type: 'enemy', x: 40, y: 27 },
    { type: 'enemy', x: 46, y: 27 },
    // Enemies - north corridor
    { type: 'enemy', x: 27, y: 8 },
    { type: 'enemy', x: 27, y: 14 },
    // Enemies - south corridor
    { type: 'enemy', x: 27, y: 40 },
    { type: 'enemy', x: 27, y: 46 },
    // Enemies - central arena
    { type: 'enemy', x: 23, y: 23 },
    { type: 'enemy', x: 31, y: 23 },
    { type: 'enemy', x: 23, y: 31 },
    { type: 'enemy', x: 31, y: 31 },
    { type: 'enemy', x: 27, y: 22 },
    { type: 'enemy', x: 27, y: 32 },
    // Portals
    { type: 'portal', x: 3, y: 27 },    // West exit to Olum Nehri
    { type: 'portal', x: 52, y: 27 },   // East exit to Lanetli Saray
  ];

  return { biome: 'underworld', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 3: Lanetli Saray - Cursed Palace
// ============================================
function createLanetliSarayTemplate(): MapTemplate {
  const W = 50, H = 55;
  const tiles = createTileGrid(W, H, TileType.VOID);
  const props: PropPlacement[] = [];

  // Palace structure - series of connected rooms
  // Entry hall (south)
  fillRect(tiles, W / 2 - 8, H - 15, 16, 12, TileType.STONE);
  fillRect(tiles, W / 2 - 6, H - 13, 12, 8, TileType.ROCKY_GROUND);

  // Main corridor
  fillRect(tiles, W / 2 - 3, 15, 6, H - 30, TileType.STONE);

  // West wing room
  fillRect(tiles, 5, 20, 16, 14, TileType.STONE);
  fillRect(tiles, 7, 22, 12, 10, TileType.ROCKY_GROUND);
  fillCorridor(tiles, 21, 27, W / 2 - 3, 27, 3, TileType.STONE);

  // East wing room
  fillRect(tiles, W - 21, 20, 16, 14, TileType.STONE);
  fillRect(tiles, W - 19, 22, 12, 10, TileType.ROCKY_GROUND);
  fillCorridor(tiles, W / 2 + 3, 27, W - 21, 27, 3, TileType.STONE);

  // Throne room (north) - boss area
  fillRect(tiles, W / 2 - 12, 4, 24, 14, TileType.STONE);
  fillRect(tiles, W / 2 - 10, 6, 20, 10, TileType.ROCKY_GROUND);
  fillCircle(tiles, W / 2, 10, 4, TileType.DIRT);

  // Props - torches along corridors
  for (let y = 18; y < H - 15; y += 6) {
    props.push({ type: 'torch', x: W / 2 - 4, y });
    props.push({ type: 'torch', x: W / 2 + 4, y });
  }

  // Pillars in throne room
  props.push({ type: 'pillar', x: W / 2 - 8, y: 7 });
  props.push({ type: 'pillar', x: W / 2 + 8, y: 7 });
  props.push({ type: 'pillar', x: W / 2 - 8, y: 13 });
  props.push({ type: 'pillar', x: W / 2 + 8, y: 13 });

  // Statue in throne room
  props.push({ type: 'statue', x: W / 2, y: 6 });

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 25, y: 50 },
    // Boss - throne room
    { type: 'boss', x: 25, y: 10 },
    // Enemies - entry hall
    { type: 'enemy', x: 22, y: 45 },
    { type: 'enemy', x: 28, y: 45 },
    // Enemies - main corridor
    { type: 'enemy', x: 25, y: 35 },
    { type: 'enemy', x: 25, y: 28 },
    { type: 'enemy', x: 25, y: 20 },
    // Enemies - west wing
    { type: 'enemy', x: 10, y: 25 },
    { type: 'enemy', x: 14, y: 28 },
    { type: 'enemy', x: 8, y: 30 },
    // Enemies - east wing
    { type: 'enemy', x: 36, y: 25 },
    { type: 'enemy', x: 40, y: 28 },
    { type: 'enemy', x: 38, y: 30 },
    // Enemies - throne room
    { type: 'enemy', x: 18, y: 10 },
    { type: 'enemy', x: 32, y: 10 },
    { type: 'enemy', x: 20, y: 15 },
    { type: 'enemy', x: 30, y: 15 },
    // Portals
    { type: 'portal', x: 25, y: 52 },   // South exit to Kemik Ormani
    { type: 'portal', x: 25, y: 5 },    // North exit to Erlik Kapisi
  ];

  return { biome: 'hell', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 4: Erlik Kapısı - Hell's Gate
// ============================================
function createErlikKapisiTemplate(): MapTemplate {
  const W = 45, H = 50;
  const tiles = createTileGrid(W, H, TileType.VOID);
  const props: PropPlacement[] = [];

  // Central platform surrounded by lava
  fillCircle(tiles, W / 2, H / 2, 16, TileType.LAVA);
  fillCircle(tiles, W / 2, H / 2, 12, TileType.ROCKY_GROUND);
  fillCircle(tiles, W / 2, H / 2, 8, TileType.STONE);

  // Entry from south
  fillRect(tiles, W / 2 - 3, H / 2 + 12, 6, 15, TileType.STONE);

  // Path to north (exit to garden)
  fillRect(tiles, W / 2 - 3, 3, 6, H / 2 - 12, TileType.STONE);

  // Lava moat around main platform
  for (let y = 3; y < H - 3; y++) {
    for (let x = 3; x < W - 3; x++) {
      const dist = Math.sqrt((x - W / 2) ** 2 + (y - H / 2) ** 2);
      if (dist > 12 && dist < 16 && tiles[y][x] !== TileType.STONE) {
        tiles[y][x] = TileType.LAVA;
      }
    }
  }

  // Small side platforms
  fillCircle(tiles, 8, 12, 4, TileType.STONE);
  fillCircle(tiles, W - 8, 12, 4, TileType.STONE);
  fillCorridor(tiles, 8, 12, W / 2 - 5, H / 2 - 8, 2, TileType.STONE);
  fillCorridor(tiles, W - 8, 12, W / 2 + 5, H / 2 - 8, 2, TileType.STONE);

  // Props
  props.push({ type: 'gate', x: W / 2, y: 5 });
  props.push({ type: 'torch', x: W / 2 - 7, y: H / 2 });
  props.push({ type: 'torch', x: W / 2 + 7, y: H / 2 });
  props.push({ type: 'statue', x: W / 2 - 4, y: H / 2 - 5 });
  props.push({ type: 'statue', x: W / 2 + 4, y: H / 2 - 5 });

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 22, y: 45 },
    // Enemies - south entry
    { type: 'enemy', x: 20, y: 42 },
    { type: 'enemy', x: 24, y: 42 },
    // Enemies - central platform
    { type: 'enemy', x: 18, y: 25 },
    { type: 'enemy', x: 26, y: 25 },
    { type: 'enemy', x: 22, y: 20 },
    { type: 'enemy', x: 22, y: 30 },
    { type: 'enemy', x: 16, y: 22 },
    { type: 'enemy', x: 28, y: 22 },
    // Enemies - north path
    { type: 'enemy', x: 22, y: 12 },
    { type: 'enemy', x: 22, y: 8 },
    // Enemies - side platforms
    { type: 'enemy', x: 8, y: 12 },
    { type: 'enemy', x: 37, y: 12 },
    // Portals
    { type: 'portal', x: 22, y: 47 },   // South exit to Lanetli Saray
    { type: 'portal', x: 22, y: 4 },    // North exit to Cehennem Bahcesi
  ];

  return { biome: 'hell', width: W, height: H, tiles, props, spawns };
}

// ============================================
// ACT 4: Cehennem Bahçesi - Hell Garden
// ============================================
function createCehennemBahcesiTemplate(): MapTemplate {
  const W = 55, H = 50;
  const tiles = createTileGrid(W, H, TileType.ROCKY_GROUND);
  const props: PropPlacement[] = [];

  // Void borders
  fillRect(tiles, 0, 0, W, 2, TileType.VOID);
  fillRect(tiles, 0, H - 2, W, 2, TileType.VOID);
  fillRect(tiles, 0, 0, 2, H, TileType.VOID);
  fillRect(tiles, W - 2, 0, 2, H, TileType.VOID);

  // Lava rivers forming a grid pattern
  for (let x = 10; x < W - 5; x += 14) {
    fillRect(tiles, x, 3, 2, H - 6, TileType.LAVA);
  }
  for (let y = 10; y < H - 5; y += 12) {
    fillRect(tiles, 3, y, W - 6, 2, TileType.LAVA);
  }

  // "Garden" islands between lava
  const islandCenters = [
    { x: 7, y: 7 }, { x: 20, y: 7 }, { x: 34, y: 7 }, { x: 47, y: 7 },
    { x: 7, y: 18 }, { x: 20, y: 18 }, { x: 34, y: 18 }, { x: 47, y: 18 },
    { x: 7, y: 30 }, { x: 20, y: 30 }, { x: 34, y: 30 }, { x: 47, y: 30 },
    { x: 7, y: 42 }, { x: 20, y: 42 }, { x: 34, y: 42 }, { x: 47, y: 42 },
  ];

  for (const ic of islandCenters) {
    if (ic.x < W - 3 && ic.y < H - 3) {
      fillCircle(tiles, ic.x, ic.y, 4, TileType.DARK_GRASS);
    }
  }

  // Main path through the garden
  fillCorridor(tiles, 4, H / 2, W - 4, H / 2, 3, TileType.STONE);
  fillCorridor(tiles, W / 2, 4, W / 2, H - 4, 3, TileType.STONE);

  // Bridges over lava
  for (let x = 10; x < W - 5; x += 14) {
    fillRect(tiles, x, H / 2 - 1, 2, 3, TileType.STONE);
  }
  for (let y = 10; y < H - 5; y += 12) {
    fillRect(tiles, W / 2 - 1, y, 3, 2, TileType.STONE);
  }

  // Dead trees on islands
  for (let i = 0; i < 10; i++) {
    const ic = islandCenters[Math.floor(Math.random() * islandCenters.length)];
    if (ic.x < W - 3 && ic.y < H - 3) {
      props.push({ type: 'tree_dead', x: ic.x + (Math.random() > 0.5 ? 1 : -1), y: ic.y + (Math.random() > 0.5 ? 1 : -1), variant: i % 2 });
    }
  }

  // Torches at intersections
  props.push({ type: 'torch', x: W / 2 - 2, y: H / 2 - 2 });
  props.push({ type: 'torch', x: W / 2 + 2, y: H / 2 - 2 });
  props.push({ type: 'torch', x: W / 2 - 2, y: H / 2 + 2 });
  props.push({ type: 'torch', x: W / 2 + 2, y: H / 2 + 2 });

  const spawns: SpawnPoint[] = [
    { type: 'player', x: 4, y: 25 },
    // Enemies - west side
    { type: 'enemy', x: 7, y: 7 },
    { type: 'enemy', x: 7, y: 18 },
    { type: 'enemy', x: 7, y: 30 },
    { type: 'enemy', x: 7, y: 42 },
    // Enemies - center column
    { type: 'enemy', x: 20, y: 7 },
    { type: 'enemy', x: 20, y: 18 },
    { type: 'enemy', x: 20, y: 30 },
    { type: 'enemy', x: 20, y: 42 },
    // Enemies - east side
    { type: 'enemy', x: 34, y: 7 },
    { type: 'enemy', x: 34, y: 18 },
    { type: 'enemy', x: 34, y: 30 },
    { type: 'enemy', x: 34, y: 42 },
    // Enemies - far east
    { type: 'enemy', x: 47, y: 7 },
    { type: 'enemy', x: 47, y: 18 },
    { type: 'enemy', x: 47, y: 30 },
    { type: 'enemy', x: 47, y: 42 },
    // Portals
    { type: 'portal', x: 3, y: 25 },    // West exit to Erlik Kapisi
    { type: 'portal', x: 50, y: 25 },   // East exit to Taht Odasi
  ];

  return { biome: 'hell', width: W, height: H, tiles, props, spawns };
}

// ============================================
// Zone to Template mapping (lazy initialization)
// ============================================
import { ZoneType } from '@shared/world/Zone';

// Template cache - created on first access
const templateCache: Map<ZoneType, MapTemplate> = new Map();

// Template factory functions
const templateFactories: Partial<Record<ZoneType, () => MapTemplate>> = {
  'otag_koyu': createOtagKoyuTemplate,
  'karanlik_orman': createKaranlikOrmanTemplate,
  'ruh_batakligi': createRuhBatakligiTemplate,
  'erlik_tapinagi': createErlikTapinagiTemplate,
  'ergenekon_girisi': createErgenekonGirisiTemplate,
  'firtina_ovasi': createFirtinaOvasiTemplate,
  'ates_magarasi': createAtesMagarasiTemplate,
  'yersub_tahti': createYersubTahtiTemplate,
  'karanlik_tunel': createKaranlikTunelTemplate,
  'olum_nehri': createOlumNehriTemplate,
  'kemik_ormani': createKemikOrmaniTemplate,
  'lanetli_saray': createLanetliSarayTemplate,
  'erlik_kapisi': createErlikKapisiTemplate,
  'cehennem_bahcesi': createCehennemBahcesiTemplate,
  'taht_odasi': createTahtOdasiTemplate,
};

// Get template for a zone (lazy creation)
export function getZoneTemplate(zoneId: ZoneType): MapTemplate | undefined {
  // Check cache first
  if (templateCache.has(zoneId)) {
    return templateCache.get(zoneId);
  }

  // Create template if factory exists
  const factory = templateFactories[zoneId];
  if (factory) {
    const template = factory();
    templateCache.set(zoneId, template);
    return template;
  }

  return undefined;
}
